import { useMemo, useRef, useState, type FormEvent } from "react";
import { apiCall, useApi, ApiError } from "@excellent/ui/api";
import { universalVerification } from "@excellent/ui/api/contracts/universal-verification";
import {
  projectUniversalCompletionOverview,
  type UniversalConsoleOverview,
} from "@excellent/core/verification-universal-surfaces-client";
import { canonicalize, type CanonicalJson } from "@excellent/core/verification-protocol-client";
import { projectCompletionAssurance } from "@excellent/core/verification-protocol-verdict";
import {
  classifyWorkSubject,
  type WorkChainSubjectClass,
} from "@excellent/ui/api/contracts/verification-work-chain";
import {
  UNIVERSAL_DETERMINISTIC_EVALUATOR_REF,
  UNIVERSAL_DETERMINISTIC_EVALUATOR_VERSION,
} from "@excellent/core/verification-universal-evaluator";
import {
  CheckCircle2,
  FileCheck2,
  Gavel,
  GitPullRequest,
  Plus,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";

import { verificationCanonicalClients } from "../../api/canonical-clients";
import { useCanonicalEnvironmentOptional } from "../../canonical-environment-provider";
import {
  canonicalWorkHref,
  canonicalWorkReadQuery,
  canonicalWorkResponseMatches,
} from "./environment-read-scope";
import {
  ActionButton,
  Callout,
  DataTable,
  Field,
  Fields,
  Input,
  Label,
  Mono,
  ScopeTabs,
  Section,
  Stat,
  StatGrid,
  Textarea,
  ToneBadge,
  Verdict,
  type DataTableColumn,
} from "../../ui";
import { href } from "../../nav";
import { resolveReceiptVerdicts, verdictFrom } from "../../resolved-verdicts";
import {
  canAuthorizeCurrentAction,
  combineCompletionHistory,
  currentAuthorityIds,
  currentProspectiveReceipts,
} from "../../completion-history";

type Overview = UniversalConsoleOverview;
type Work = Overview["work"][number];
type Contract = Overview["contracts"][number];
type Attempt = Overview["attempts"][number];
type Receipt = Overview["receipts"][number];
type Amendment = Overview["amendments"][number];
type Review = Overview["reviews"][number];
type LearningProposal = Overview["learningProposals"][number];
type CompletionProposalEngine = NonNullable<Overview["completionProposalEngine"]>;
type CompletionProposalExample = CompletionProposalEngine["examples"][number];
type CompletionTemplate = CompletionProposalEngine["templates"][number];
type Adapter = Overview["adapters"][number];
type DurableLoopJob = Overview["durableLoopJobs"][number];
type OperationalJob = Overview["operationalDurability"]["projections"][number];
type EnvironmentBindingProof = Overview["environmentBindingProof"][number];
type Tab = "work" | "contracts" | "attempts" | "receipts" | "reviews" | "adapters" | "operations";
const TABS: readonly Tab[] = [
  "work",
  "contracts",
  "attempts",
  "receipts",
  "reviews",
  "adapters",
  "operations",
];

/** This view only uses a currently observed contract; URL references confer no authority. */
export function CodingWorkerHandoff({
  contract,
  workerActorId,
  workerAvailable,
  attempts,
  onRefresh,
  workHref,
}: {
  contract: Contract;
  workerActorId: string;
  workerAvailable: boolean;
  attempts: Attempt[];
  onRefresh: () => Promise<unknown>;
  workHref: string;
}) {
  const [snapshot, setSnapshot] = useState("");
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [created, setCreated] = useState<Attempt | null>(null);
  const [copied, setCopied] = useState(false);
  const inFlight = useRef(false);
  const retry = useRef<{ snapshot: string; key: string } | null>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const attempt =
    attempts.find(
      (item) =>
        item.binding.contractDigest === contract.contractDigest &&
        item.worker.actorId === workerActorId &&
        (item.state === "OPEN" || item.state === "SUBMITTED"),
    ) ?? created;
  const instructions = [
    `Task: ${contract.content.title}`,
    `Assigned worker: ${workerActorId}`,
    `Work: ${contract.content.workItemId}`,
    `Accepted contract: ${contract.content.contractId} @ ${contract.content.version}`,
    `Contract digest: ${contract.contractDigest}`,
    ...(attempt
      ? [
          `Attempt: ${attempt.binding.attemptId}`,
          `Input snapshot: ${attempt.binding.inputSnapshotDigest}`,
        ]
      : []),
    "Read the accepted contract and its allowed paths before changing files. Use your own scoped worker credential; never use the requester's browser session.",
    "Work within this attempt, submit the output snapshot and evidence, then wait for independent verification. An open attempt is not execution or a verified result.",
  ].join("\n");

  async function open(event: FormEvent) {
    event.preventDefault();
    if (
      inFlight.current ||
      attempt ||
      !workerAvailable ||
      !/^sha256:[a-f0-9]{64}$/.test(snapshot.trim())
    )
      return;
    inFlight.current = true;
    setPending(true);
    setFailure(null);
    const value = snapshot.trim();
    if (retry.current?.snapshot !== value)
      retry.current = { snapshot: value, key: commandKey("coding:open-attempt") };
    try {
      const result = await apiCall(universalVerification.openAttempt, {
        body: {
          idempotencyKey: retry.current.key,
          contractId: contract.content.contractId,
          contractVersion: contract.content.version,
          workerActorId,
          inputSnapshotDigest: value,
          supersedesAttemptId: null,
        },
      });
      setCreated(result);
      void onRefresh().catch(() => {});
    } catch (cause) {
      setFailure(
        cause instanceof ApiError && cause.status === 401
          ? "Sign in in a new tab, return here, and retry. Keep this tab open to retain the same request."
          : `The attempt was not confirmed. Refresh its status before retrying this unchanged request. ${cause instanceof Error ? cause.message : "No response was received."}`,
      );
    } finally {
      inFlight.current = false;
      setPending(false);
      setTimeout(() => statusRef.current?.focus(), 0);
    }
  }

  return (
    <div
      className="flex min-w-0 flex-col gap-6"
      data-coding-worker-handoff={attempt ? "recorded" : "prepare"}
    >
      <Section
        title={attempt ? "Worker attempt recorded" : "Prepare worker attempt"}
        note="Requester accepts the task → worker performs the attempt → independent verifier checks the result."
      >
        <h2 className="break-words type-body font-medium">{contract.content.title}</h2>
        <Fields>
          <Field label="Contract">Version {contract.content.version} · accepted</Field>
          <Field label="Assigned worker">
            <span className="break-all">{workerActorId || "No worker selected"}</span>
          </Field>
        </Fields>
      </Section>
      {attempt ? (
        <>
          <div ref={statusRef} tabIndex={-1} role="status">
            <Callout
              tone="accent"
              title={
                attempt.state === "SUBMITTED"
                  ? "Worker output submitted"
                  : "Next: share the task with your worker"
              }
            >
              {attempt.state === "SUBMITTED"
                ? "This attempt already has submitted output. Open the task to inspect evidence and verification status."
                : "The attempt is recorded. This action did not launch the worker. Copy the instructions below into the assigned worker's session, then follow its progress on the saved task."}
            </Callout>
          </div>
          <Section
            title="Worker instructions"
            note="These instructions contain task references, not credentials. Use the worker's existing authorized connection."
          >
            <Textarea
              aria-label="Worker instructions"
              readOnly
              value={instructions}
              rows={9}
              className="w-full min-w-0 break-all"
            />
            <div className="flex flex-wrap gap-2">
              <ActionButton
                actionId="coding-handoff.copy"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(instructions);
                    setCopied(true);
                  } catch {
                    setFailure(
                      "Clipboard access is unavailable. Select and copy the worker instructions above.",
                    );
                  }
                }}
              >
                {copied ? "Instructions copied" : "Copy worker instructions"}
              </ActionButton>
              <a
                className="inline-flex min-h-9 items-center rounded-md border border-border px-3 type-body"
                href={workHref}
              >
                View attempt and verification status
              </a>
            </div>
          </Section>
        </>
      ) : (
        <form onSubmit={(event) => void open(event)} className="flex min-w-0 flex-col gap-4">
          {!workerAvailable && (
            <Callout tone="unknown" title="Worker connection required">
              This assigned worker is not present in the current worker list. Ask the workspace
              owner to register or reconnect this exact worker, then refresh here. Selecting a
              different worker requires a reviewed contract.
            </Callout>
          )}
          <div className={fieldClass}>
            <Label htmlFor="coding-worker-snapshot">Worker's input snapshot digest</Label>
            <Input
              id="coding-worker-snapshot"
              value={snapshot}
              onChange={(event) => setSnapshot(event.target.value)}
              disabled={pending}
              required
              pattern="sha256:[a-f0-9]{64}"
              placeholder="sha256:…"
              aria-describedby="coding-worker-snapshot-hint"
            />
            <p id="coding-worker-snapshot-hint" className="type-subtext text-muted-foreground">
              Ask the assigned worker for the SHA-256 digest of its captured repository input,
              before any changes. Paste that exact digest; a task ID or Git commit hash is not an
              input snapshot.
            </p>
          </div>
          <ActionButton
            actionId="coding-handoff.open"
            type="submit"
            variant="primary"
            disabled={pending || !workerAvailable || !/^sha256:[a-f0-9]{64}$/.test(snapshot.trim())}
            reason="The assigned worker and its actual input snapshot are required."
          >
            {pending ? "Recording worker attempt…" : "Open worker attempt"}
          </ActionButton>
          <p className="type-subtext text-muted-foreground">
            This records the attempt against the accepted contract. It does not execute code,
            deliver credentials, or verify the result.
          </p>
          <div ref={statusRef} tabIndex={-1} role="status">
            {pending ? "Waiting for the server to confirm the attempt. Keep this tab open." : null}
          </div>
        </form>
      )}
      {failure && (
        <Callout tone="unknown" title="Action needs attention">
          <p role="alert" className="break-words">
            {failure}
          </p>
          <a href="/login" target="_blank" rel="noopener noreferrer" className="underline">
            Sign in in a new tab
          </a>
        </Callout>
      )}
      <div className="flex flex-wrap gap-2">
        <ActionButton
          actionId="coding-handoff.refresh"
          disabled={pending}
          onClick={async () => {
            try {
              await onRefresh();
            } catch {
              setFailure(
                "Status could not be refreshed. Check your connection and try again; the recorded state is unconfirmed.",
              );
            }
          }}
        >
          Refresh attempt status
        </ActionButton>
        <a
          href={workHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-9 items-center px-3 type-body underline"
        >
          Inspect saved task in a new tab
        </a>
      </div>
    </div>
  );
}

const fieldClass = "flex min-w-0 flex-col gap-1.5";
const selectClass =
  "h-9 min-w-0 rounded-md border border-border bg-background px-3 type-body text-foreground";
const RECEIPT_FRESHNESS_SECONDS = 86_400;
const CONSOLE_RELEASE_EVIDENCE_CLASS = "internal_dogfood" as const;
const HUMAN_ORACLE_POLICY_REF = "policy:human-oracle:release";
const HUMAN_ORACLE_POLICY_VERSION = "1";

/** Short row/tab label for a Work subject class — mirrors the receipts ledger's
 * own label map so the same class reads identically on both pages. */
const SUBJECT_CLASS_SHORT_LABEL: Record<WorkChainSubjectClass, string> = {
  "real-subject": "Real subject",
  "self-test-fixture": "Self-test",
  "synthetic-fixture": "Synthetic",
};

function workSubjectTone(subjectClass: WorkChainSubjectClass): "accent" | "unknown" {
  return subjectClass === "real-subject" ? "accent" : "unknown";
}

function commandKey(prefix: string): string {
  return `${prefix}:${crypto.randomUUID()}`;
}

async function digest(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const hashed = await crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${Array.from(new Uint8Array(hashed), (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

async function canonicalDigest(value: CanonicalJson): Promise<string> {
  return digest(canonicalize(value));
}

function shortDigest(value: string): string {
  return `${value.slice(0, 18)}...${value.slice(-8)}`;
}

function initialTab(): Tab {
  if (typeof window === "undefined") return "work";
  const candidate = new URLSearchParams(window.location.search).get("tab");
  return TABS.includes(candidate as Tab) ? (candidate as Tab) : "work";
}

function workerPrincipals(data: Overview | undefined) {
  return data?.principals.filter((principal) => principal.roles.includes("worker")) ?? [];
}

function receiptCompletionSummary(receipt: Receipt) {
  return "completionSummary" in receipt.payload ? receipt.payload.completionSummary : null;
}

function replayProofTone(data: Overview["independentReplayProof"]): "pass" | "unknown" {
  return data.independentArtifactCount > 0 &&
    data.replayCommandCount > 0 &&
    data.unverifiedArtifactCount === 0
    ? "pass"
    : "unknown";
}

/**
 * What a learning proposal's SOURCE receipt was, in one phrase.
 *
 * `sourceReceipt.aggregateStatus` is a COPY of the receipt's verdict, frozen into the
 * proposal when it was minted — and `createLearningProposal` now resolves that copy
 * through the verdict law at `proposedAt`, so what is stored here is already a
 * read-time answer and this function must not re-resolve it. (It could not anyway: the
 * proposal's `sourceReceipt` projection carries no determinations, hence no deadline.)
 *
 * THE TAIL IS A PASS TEST, NOT A FALLBACK. It used to read "any status I have not heard
 * of is `Verified positive source`", which is the most expensive direction a tail can
 * point: PENDING was added to the vocabulary after this ladder was written, so a
 * not-yet-due source was advertised to a person as VERIFIED. Anything this function
 * cannot name is now reported as unrecognized rather than as success.
 */
export function learningSourceCalibration(row: LearningProposal): string {
  if (row.sourceReceipt.aggregateStatus === "PASS") return "Verified positive source";
  if (row.sourceReceipt.aggregateStatus === "MISMATCH") return "Contradicted outcome retained";
  if (row.sourceReceipt.aggregateStatus === "FAIL") return "Negative outcome retained";
  if (row.sourceReceipt.aggregateStatus === "UNKNOWN") return "Uncertain outcome retained";
  if (row.sourceReceipt.aggregateStatus === "NOT_RUN") return "Not-run outcome retained";
  if (row.sourceReceipt.aggregateStatus === "PENDING") return "Not-yet-due outcome retained";
  return "Unrecognized outcome retained";
}

export function UniversalProtocolConsole({
  now = new Date().toISOString(),
}: {
  /**
   * THE RENDER EDGE. One clock read for the whole console. Every verdict below — the
   * receipts table, the consequence preview, the adapter's signed basis, the
   * prospective-PASS tile — reads `resolvedVerdicts` built from THIS instant, so no two
   * panels on this screen can disagree about whether a deadline has passed.
   */
  now?: string;
} = {}) {
  const canonicalContext = useCanonicalEnvironmentOptional();
  const workQuery = canonicalWorkReadQuery(canonicalContext);
  const localEnvironmentReady = Boolean(workQuery);
  const overview = useApi(
    verificationCanonicalClients.workLedger.operation,
    workQuery ? { query: workQuery } : null,
  );
  const [tab, setTab] = useState<Tab>(initialTab);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [workType, setWorkType] = useState("code_change");
  const [sourceUri, setSourceUri] = useState("https://github.com/example/repository/issues/1");
  const [workItemId, setWorkItemId] = useState(() =>
    typeof window === "undefined"
      ? ""
      : (new URLSearchParams(window.location.search).get("workId") ?? ""),
  );
  const [workerActorId, setWorkerActorId] = useState(() =>
    typeof window === "undefined"
      ? ""
      : (new URLSearchParams(window.location.search).get("workerActorId") ?? ""),
  );
  const [handoff] = useState(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    return params.get("handoff") === "1"
      ? {
          workId: params.get("workId") ?? "",
          workerActorId: params.get("workerActorId") ?? "",
          contractDigest: params.get("contractDigest") ?? "",
        }
      : null;
  });
  const [predicateKind, setPredicateKind] = useState<
    "artifact_present" | "exit_code" | "text_contains"
  >("exit_code");
  const [predicateDescription, setPredicateDescription] = useState(
    "The independently replayed command exits successfully.",
  );
  const [expectedValue, setExpectedValue] = useState("0");
  const [evidenceKind, setEvidenceKind] = useState("command_log");
  const [selectedReceiptId, setSelectedReceiptId] = useState<string | null>(null);
  const [reviewKind, setReviewKind] = useState<"HUMAN_CHECK" | "APPEAL" | "DISPUTE">("APPEAL");
  const [reviewReason, setReviewReason] = useState("");
  const [humanStatus, setHumanStatus] = useState<"PASS" | "FAIL" | "UNKNOWN" | "NOT_RUN">(
    "UNKNOWN",
  );

  const responseCurrent = canonicalWorkResponseMatches(overview.data, workQuery);
  const data = responseCurrent ? overview.data : undefined;
  const completionProjection = useMemo(
    () => (data ? projectUniversalCompletionOverview(data) : null),
    [data],
  );
  const readableWork = useMemo(
    () =>
      completionProjection
        ? combineCompletionHistory(
            completionProjection.current.work,
            completionProjection.incomplete.work,
            (work) => work.id,
          )
        : [],
    [completionProjection],
  );
  // The same classifier the receipts ledger and the per-item chain view use —
  // `classifyWorkSubject` — applied here so the Work ledger list can also
  // distinguish the product's own fixtures from real work, not just the
  // per-item detail route.
  const [workSubjectFilter, setWorkSubjectFilter] = useState<"all" | WorkChainSubjectClass>("all");
  const workSubjectCounts = useMemo(() => {
    const counts: Record<WorkChainSubjectClass, number> = {
      "real-subject": 0,
      "self-test-fixture": 0,
      "synthetic-fixture": 0,
    };
    for (const row of readableWork) counts[classifyWorkSubject(row.source.uri).class] += 1;
    return counts;
  }, [readableWork]);
  const filteredWork = useMemo(() => {
    if (workSubjectFilter === "all") return readableWork;
    return readableWork.filter(
      (row) => classifyWorkSubject(row.source.uri).class === workSubjectFilter,
    );
  }, [readableWork, workSubjectFilter]);
  const readableContracts = useMemo(
    () =>
      completionProjection
        ? combineCompletionHistory(
            completionProjection.current.contracts,
            completionProjection.incomplete.contracts,
            (contract) => contract.contractDigest,
          )
        : [],
    [completionProjection],
  );
  const readableAttempts = useMemo(
    () =>
      completionProjection
        ? combineCompletionHistory(
            completionProjection.current.attempts,
            completionProjection.incomplete.attempts,
            (attempt) => attempt.binding.attemptId,
          )
        : [],
    [completionProjection],
  );
  const readableReceipts = useMemo(
    () =>
      completionProjection
        ? combineCompletionHistory(
            completionProjection.current.receipts,
            completionProjection.incomplete.receipts,
            (receipt) => receipt.payload.id,
          )
        : [],
    [completionProjection],
  );
  const readableReviews = useMemo(
    () =>
      completionProjection
        ? combineCompletionHistory(
            completionProjection.current.reviews,
            completionProjection.incomplete.reviews,
            (review) => review.request.id,
          )
        : [],
    [completionProjection],
  );
  const readableAmendments = useMemo(
    () =>
      completionProjection
        ? combineCompletionHistory(
            completionProjection.current.amendments,
            completionProjection.incomplete.amendments,
            (amendment) => amendment.proposal.id,
          )
        : [],
    [completionProjection],
  );
  const readableLearningProposals = useMemo(
    () =>
      completionProjection
        ? combineCompletionHistory(
            completionProjection.current.learningProposals,
            completionProjection.incomplete.learningProposals,
            (proposal) => proposal.id,
          )
        : [],
    [completionProjection],
  );
  const completionProposalEngine = data?.completionProposalEngine;
  const currentWorkIds = useMemo(
    () => currentAuthorityIds(completionProjection?.current.work ?? [], (work) => work.id),
    [completionProjection],
  );
  const currentContractDigests = useMemo(
    () =>
      currentAuthorityIds(
        completionProjection?.current.contracts ?? [],
        (contract) => contract.contractDigest,
      ),
    [completionProjection],
  );
  const currentAttemptIds = useMemo(
    () =>
      currentAuthorityIds(
        completionProjection?.current.attempts ?? [],
        (attempt) => attempt.binding.attemptId,
      ),
    [completionProjection],
  );
  // READ TIME, once for the console. A signed receipt is append-only, so a PENDING one
  // keeps saying PENDING past its deadline; `resolveStoredVerdict` (via
  // `resolveReceiptVerdicts`) is the one engine that says what it means today, and this
  // file holds no deadline arithmetic of its own.
  const resolvedVerdicts = useMemo(
    () => resolveReceiptVerdicts(readableReceipts, now),
    [readableReceipts, now],
  );
  const currentReceiptIds = useMemo(
    () =>
      currentAuthorityIds(
        completionProjection?.current.receipts ?? [],
        (receipt) => receipt.payload.id,
      ),
    [completionProjection],
  );
  const prospectiveReceiptIds = useMemo(
    () =>
      currentAuthorityIds(
        currentProspectiveReceipts(completionProjection?.current.receipts ?? []),
        (receipt) => receipt.payload.id,
      ),
    [completionProjection],
  );
  const currentReviewIds = useMemo(
    () =>
      currentAuthorityIds(
        completionProjection?.current.reviews ?? [],
        (review) => review.request.id,
      ),
    [completionProjection],
  );
  const currentAmendmentIds = useMemo(
    () =>
      currentAuthorityIds(
        completionProjection?.current.amendments ?? [],
        ({ proposal }) => proposal.id,
      ),
    [completionProjection],
  );
  const incompleteRecordCount = completionProjection
    ? Object.values(completionProjection.incomplete).reduce(
        (sum, records) => sum + records.length,
        0,
      )
    : 0;
  const workers = workerPrincipals(data);
  const selectedWorker = workerActorId || workers[0]?.actorId || "";
  const selectedWork = workItemId || completionProjection?.current.work[0]?.id || "";
  const selectedWorkRecord = completionProjection?.current.work.find(
    (work) => work.id === selectedWork,
  );
  const selectedReceipt =
    readableReceipts.find((receipt) => receipt.payload.id === selectedReceiptId) ??
    completionProjection?.current.receipts.find(
      (receipt) => receipt.payload.assurance === "PROSPECTIVE",
    ) ??
    completionProjection?.current.receipts[0] ??
    readableReceipts[0] ??
    null;
  const selectedReceiptIsCurrent = Boolean(
    selectedReceipt && canAuthorizeCurrentAction(selectedReceipt.payload.id, currentReceiptIds),
  );
  const selectedReceiptCanAuthorize = Boolean(
    selectedReceipt && canAuthorizeCurrentAction(selectedReceipt.payload.id, prospectiveReceiptIds),
  );
  const receiptDetail = useApi(
    universalVerification.receipt,
    selectedReceipt && workQuery
      ? { params: { id: selectedReceipt.payload.id }, query: workQuery }
      : null,
  );

  async function run<T>(
    name: string,
    action: () => Promise<T>,
    success: string,
  ): Promise<T | null> {
    setBusy(name);
    setError(null);
    setNotice(null);
    try {
      const result = await action();
      setNotice(success);
      await overview.mutate();
      return result;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function createWork(event: FormEvent) {
    event.preventDefault();
    const sourceDigest = await digest(JSON.stringify({ title, description, sourceUri }));
    const requestedOutcomeId = `outcome:${crypto.randomUUID()}`;
    const created = await run(
      "create-work",
      () =>
        apiCall(universalVerification.createWork, {
          query: workQuery ?? { env: "real" },
          body: {
            idempotencyKey: commandKey("console:create-work"),
            projectId: null,
            workType,
            title,
            description,
            completionIntent: {
              kind: "task_outcomes",
              outcomes: [
                {
                  id: requestedOutcomeId,
                  statement: description,
                  sourceSpan: `${sourceUri}#requested-outcome`,
                },
              ],
            },
            consequence: "high",
            source: { kind: "work_request", uri: sourceUri, digest: sourceDigest },
          },
        }),
      "Work created.",
    );
    if (created) {
      setWorkItemId(created.id);
      setTitle("");
      setDescription("");
    }
  }

  async function compileContract(event: FormEvent) {
    event.preventDefault();
    if (!data) {
      setError("Universal verification data is still loading.");
      return;
    }
    const coversOutcomeIds =
      selectedWorkRecord?.completionIntent?.kind === "task_outcomes"
        ? selectedWorkRecord.completionIntent.outcomes.map((outcome) => outcome.id).sort()
        : [];
    if (coversOutcomeIds.length === 0) {
      setError("The selected work has no requested task outcome to cover.");
      return;
    }
    if (!selectedWorkRecord) {
      setError("Select a Work item before compiling a Contract.");
      return;
    }
    const parameters: Record<string, string | number> =
      predicateKind === "exit_code"
        ? { expected: Number(expectedValue) }
        : predicateKind === "text_contains"
          ? { needle: expectedValue }
          : {};
    const version = String(
      (data?.contracts.filter((contract) => contract.content.workItemId === selectedWork).length ??
        0) + 1,
    );
    const fallbackSource = {
      id: `source:${selectedWorkRecord.id}`,
      kind: "work_request" as const,
      uri: selectedWorkRecord.source.uri,
      digest: selectedWorkRecord.source.digest,
      precedence: 1,
    };
    const evaluatorSpec = {
      ref: UNIVERSAL_DETERMINISTIC_EVALUATOR_REF,
      version: UNIVERSAL_DETERMINISTIC_EVALUATOR_VERSION,
      predicates: [
        "artifact_present",
        "content_digest_equals",
        "exit_code",
        "json_equals",
        "resource_bounds",
        "text_contains",
      ],
      failureMode: "NOT_RUN",
    } as const;
    const obligation: Contract["content"]["obligations"][number] = {
      id: `obligation:${crypto.randomUUID()}`,
      classification: "task_outcome",
      sourceIds: [fallbackSource.id],
      coversOutcomeIds,
      subject: `work:${selectedWork}:output`,
      predicate: { kind: predicateKind, description: predicateDescription, parameters },
      successCondition:
        predicateKind === "exit_code"
          ? `The observed exit code equals ${expectedValue}.`
          : predicateKind === "text_contains"
            ? `The admitted evidence contains ${expectedValue}.`
            : `At least one admitted ${evidenceKind} artifact is present.`,
      falsifier:
        predicateKind === "artifact_present"
          ? `No admitted ${evidenceKind} artifact is present.`
          : `The observed evidence does not satisfy the success condition.`,
      required: true,
      evidence: [
        {
          kind: evidenceKind,
          minimumCount: 1,
          maximumAgeSeconds: 900,
          requiresIndependentCollection: true,
        },
      ],
      evaluator: {
        ref: UNIVERSAL_DETERMINISTIC_EVALUATOR_REF,
        version: UNIVERSAL_DETERMINISTIC_EVALUATOR_VERSION,
        digest: await canonicalDigest(evaluatorSpec),
        authority: "deterministic",
        rubricDigest: null,
        requiresIndependentExecution: true,
      },
      oracles: [],
    };
    const reviewedAt = new Date().toISOString();
    const reviewId = `review:${selectedWork}:${version}`;
    const sourceReviewBinding = {
      schema: "excellent.universal.contract-source-review-binding.v1",
      reviewId,
      reviewedAt,
      work: {
        tenantId: selectedWorkRecord.tenantId,
        workItemId: selectedWorkRecord.id,
        workType: selectedWorkRecord.workType,
        source: selectedWorkRecord.source,
        completionIntent: selectedWorkRecord.completionIntent,
      },
      contract: {
        contractId: `contract:${selectedWorkRecord.id}`,
        version,
        deploymentMode: "REVIEW_GATED",
        expectedWorkerActorId: selectedWorker,
        sources: [fallbackSource],
        obligations: [obligation],
      },
      reviewer: data.currentActor,
    } satisfies CanonicalJson;
    const reviewDigest = await canonicalDigest(sourceReviewBinding);
    await run(
      "compile-contract",
      () =>
        apiCall(universalVerification.compileContract, {
          body: {
            idempotencyKey: commandKey("console:compile-contract"),
            workItemId: selectedWork,
            version,
            expectedWorkerActorId: selectedWorker,
            deploymentMode: "REVIEW_GATED",
            sources: [fallbackSource],
            obligations: [obligation],
            sourceReview: {
              reviewId,
              reviewedAt,
              reviewDigest,
            },
          },
        }),
      "Contract compiled, accepted, and frozen.",
    );
  }

  async function openAttempt(contract: Contract) {
    await run(
      `attempt:${contract.contractDigest}`,
      async () =>
        apiCall(universalVerification.openAttempt, {
          body: {
            idempotencyKey: commandKey("console:open-attempt"),
            contractId: contract.content.contractId,
            contractVersion: contract.content.version,
            workerActorId: selectedWorker,
            inputSnapshotDigest: await digest(`input:${contract.contractDigest}:${Date.now()}`),
            supersedesAttemptId: null,
          },
        }),
      "Attempt bound to the authenticated worker.",
    );
  }

  async function decideAmendment(amendment: Amendment, decision: "ACCEPTED" | "REJECTED") {
    if (!currentAmendmentIds.has(amendment.proposal.id)) {
      setError("Historical amendments are incomplete and cannot authorize a successor contract.");
      return;
    }
    await run(
      `amendment:${amendment.proposal.id}`,
      async () =>
        apiCall(universalVerification.decideAmendment, {
          body: {
            idempotencyKey: commandKey("console:decide-amendment"),
            amendmentId: amendment.proposal.id,
            decision,
            workerActorId: selectedWorker,
            reason:
              decision === "ACCEPTED"
                ? "Accepted by named console authority."
                : "Rejected by named console authority.",
            authorityBasis: "named_human",
            deploymentMode: decision === "ACCEPTED" ? "REVIEW_GATED" : undefined,
            inputSnapshotDigest:
              decision === "ACCEPTED"
                ? await digest(
                    `amendment-input:${amendment.proposal.proposedContentDigest}:${Date.now()}`,
                  )
                : undefined,
          },
        }),
      `Amendment ${decision.toLowerCase()}.`,
    );
  }

  async function requestReview(event: FormEvent) {
    event.preventDefault();
    if (!selectedReceipt) return;
    if (!selectedReceiptIsCurrent) {
      setError("Historical receipts are incomplete and cannot authorize a new review request.");
      return;
    }
    const firstDetermination = selectedReceipt.payload.determinations[0];
    await run(
      "request-review",
      () =>
        apiCall(universalVerification.requestReview, {
          body: {
            idempotencyKey: commandKey("console:request-review"),
            receiptId: selectedReceipt.payload.id,
            kind: reviewKind,
            reason: reviewReason,
            assignedAuthorityActorId: null,
            humanCheck:
              reviewKind === "HUMAN_CHECK" && firstDetermination
                ? {
                    obligationId: firstDetermination.obligationId,
                    instruction: reviewReason,
                    evidenceArtifactIds: firstDetermination.evidenceArtifactIds,
                  }
                : null,
          },
        }),
      "Review opened.",
    );
    setReviewReason("");
  }

  async function resolveReview(review: Review) {
    if (!data) return;
    if (!currentReviewIds.has(review.request.id)) {
      setError("Historical reviews are incomplete and cannot be resolved as current authority.");
      return;
    }
    const humanCheck = review.request.humanCheck;
    const humanPolicyDigest = await digest(HUMAN_ORACLE_POLICY_REF);
    const humanEvidenceArtifactIds = humanCheck?.evidenceArtifactIds ?? [];
    await run(
      `review:${review.request.id}`,
      () =>
        apiCall(universalVerification.resolveReview, {
          body: {
            idempotencyKey: commandKey("console:resolve-review"),
            reviewId: review.request.id,
            outcome: review.request.kind === "HUMAN_CHECK" ? "NEW_RUN_REQUIRED" : "NO_CHANGE",
            humanDetermination: humanCheck
              ? {
                  obligationId: humanCheck.obligationId,
                  status: humanStatus,
                  evidenceArtifactIds: humanEvidenceArtifactIds,
                  rationale: `${review.request.reason} Policy ${HUMAN_ORACLE_POLICY_REF}@${HUMAN_ORACLE_POLICY_VERSION} ${humanPolicyDigest}; reviewer boundary ${data.currentActor.executionBoundaryId}.`,
                }
              : null,
            reason:
              review.request.kind === "HUMAN_CHECK"
                ? "Named human check completed; a new verification run is required."
                : "Review resolved without mutating the original receipt.",
          },
        }),
      "Review resolved.",
    );
  }

  async function executeAdapter(adapter: Adapter) {
    if (!selectedReceipt) return;
    const runtime = data?.adapterRuntime.find((candidate) => candidate.kind === adapter.kind);
    if (!runtime || runtime.delivery !== "INTENT_ONLY" || runtime.externalEffectsPossible) {
      setError(
        "Live delivery is disabled until the server returns an exact action and target preview and the operator explicitly confirms both.",
      );
      return;
    }
    if (!selectedReceiptCanAuthorize) {
      setError(
        "Only a completion-bound prospective receipt can authorize adapter execution; retrospective and incomplete receipts remain recorded facts.",
      );
      return;
    }
    const action = adapter.supportedActions.find((candidate) => candidate !== "none") ?? "none";
    await run(
      `adapter:${adapter.kind}`,
      () =>
        apiCall(universalVerification.executeRail, {
          body: {
            idempotencyKey: commandKey("console:rail-intent"),
            adapter: adapter.kind,
            receiptId: selectedReceipt.payload.id,
            target: `${adapter.kind}://console/${selectedReceipt.payload.binding.workItemId}`,
            policy: {
              ref: `policy:${adapter.kind}:console`,
              version: "1",
              digest: selectedReceipt.payload.policy.digest,
              pass: "ALLOW",
              fail: "BLOCK",
              unknown: "HOLD",
              notRun: "RETURN",
              requireProspectiveForAction: true,
              railActionOnAllow: action,
            },
          },
        }),
      `${adapter.kind} action intent recorded. No external delivery was attempted.`,
    );
  }

  const workColumns: DataTableColumn<Work>[] = [
    {
      key: "work",
      header: "Work",
      render: (row) => (
        <div>
          <strong>
            <a
              href={canonicalWorkHref(href("work-ledger", row.id), canonicalContext)}
              className="hover:underline"
              data-action-id="work.open-detail"
              aria-label={`Work detail: ${row.title}`}
            >
              {row.title}
            </a>
          </strong>
          {/* The title is the row's identity; the id stays one disclosure away. */}
          <details className="type-subtext text-muted-foreground">
            <summary className="cursor-pointer">Reference ID</summary>
            <div className="mt-1 break-all" data-work-ids>
              <Mono>{row.id}</Mono>
            </div>
          </details>
        </div>
      ),
    },
    { key: "type", header: "Type", render: (row) => row.workType },
    {
      key: "completion",
      header: "Requested outcome",
      render: (row) =>
        !row.completionIntent
          ? "Legacy row — completion definition missing"
          : row.completionIntent.kind === "task_outcomes"
            ? row.completionIntent.outcomes.map((outcome) => outcome.statement).join("; ")
            : row.completionIntent.rationale,
    },
    {
      key: "state",
      header: "State",
      render: (row) =>
        currentWorkIds.has(row.id) ? (
          <ToneBadge tone={row.state === "CLOSED" ? "pass" : "neutral"}>{row.state}</ToneBadge>
        ) : (
          <div>
            <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
            <div className="type-subtext text-muted-foreground">Recorded {row.state}</div>
          </div>
        ),
    },
    {
      key: "source",
      header: "Source",
      render: (row) => <Mono>{shortDigest(row.source.digest)}</Mono>,
    },
    {
      key: "subject",
      header: "Subject",
      render: (row) => {
        const subject = classifyWorkSubject(row.source.uri);
        return (
          <ToneBadge tone={workSubjectTone(subject.class)}>
            {SUBJECT_CLASS_SHORT_LABEL[subject.class]}
          </ToneBadge>
        );
      },
    },
    { key: "consequence", header: "Consequence", render: (row) => row.consequence },
  ];
  const contractColumns: DataTableColumn<Contract>[] = [
    {
      key: "contract",
      header: "Frozen contract",
      render: (row) => (
        <div>
          <strong>{row.content.title}</strong>
          <div className="type-subtext text-muted-foreground">
            <Mono>
              {row.content.contractId}@{row.content.version}
            </Mono>
          </div>
        </div>
      ),
    },
    {
      key: "digest",
      header: "Digest",
      render: (row) => <Mono>{shortDigest(row.contractDigest)}</Mono>,
    },
    {
      key: "predicates",
      header: "Completion coverage",
      render: (row) =>
        row.content.obligations
          .map(
            (obligation) =>
              `${"classification" in obligation ? obligation.classification : "legacy_unbound"}: ${obligation.predicate.description}${"coversOutcomeIds" in obligation && obligation.coversOutcomeIds.length ? ` → ${obligation.coversOutcomeIds.join(", ")}` : ""}`,
          )
          .join("; "),
    },
    {
      key: "authority",
      header: "Accepted by",
      render: (row) =>
        currentContractDigests.has(row.contractDigest) ? (
          <Mono>{row.acceptance.acceptedBy.actorId}</Mono>
        ) : (
          <div>
            <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
            <div className="type-subtext text-muted-foreground">
              Recorded {row.acceptance.acceptedBy.actorId}
            </div>
          </div>
        ),
    },
    {
      key: "action",
      header: "",
      render: (row) => (
        <ActionButton
          actionId="universal.open-attempt"
          size="sm"
          disabled={
            !canAuthorizeCurrentAction(
              row.contractDigest,
              currentContractDigests,
              Boolean(selectedWorker),
            )
          }
          reason={
            !currentContractDigests.has(row.contractDigest)
              ? "Historical contract — current completion binding is incomplete."
              : "An authenticated worker principal is required."
          }
          onClick={() => void openAttempt(row)}
        >
          <GitPullRequest className="size-4" /> Attach
        </ActionButton>
      ),
    },
  ];
  const attemptColumns: DataTableColumn<Attempt>[] = [
    // Plain-language first: the row leads with the work that was attempted, not the
    // attempt's uuid. Every identifier is still on the row, under "Reference IDs".
    {
      key: "work",
      header: "Work",
      cellClassName: "whitespace-normal",
      render: (row) => {
        const work = readableWork.find((item) => item.id === row.binding.workItemId);
        return (
          <div className="min-w-0" data-attempt-id={row.binding.attemptId}>
            <strong data-attempt-plain>
              {work?.title ?? "Work item outside the current ledger"}
            </strong>
            <details className="type-subtext text-muted-foreground">
              <summary className="cursor-pointer">Reference IDs</summary>
              <div className="mt-1 flex min-w-0 flex-col gap-0.5 break-all" data-attempt-ids>
                <Mono>Attempt {row.binding.attemptId}</Mono>
                <Mono>Work {row.binding.workItemId}</Mono>
                <Mono>Contract {shortDigest(row.binding.contractDigest)}</Mono>
              </div>
            </details>
          </div>
        );
      },
    },
    { key: "worker", header: "Worker", render: (row) => <Mono>{row.worker.actorId}</Mono> },
    {
      key: "contract",
      header: "Contract",
      render: (row) => {
        const contract = readableContracts.find(
          (item) => item.contractDigest === row.binding.contractDigest,
        );
        return contract ? (
          <div className="min-w-0">
            <div>{contract.content.title}</div>
            <div className="type-subtext text-muted-foreground">v{contract.content.version}</div>
          </div>
        ) : (
          <Mono>{shortDigest(row.binding.contractDigest)}</Mono>
        );
      },
    },
    {
      key: "state",
      header: "State",
      render: (row) =>
        currentAttemptIds.has(row.binding.attemptId) ? (
          <ToneBadge tone={row.state === "SUBMITTED" ? "pass" : "neutral"}>{row.state}</ToneBadge>
        ) : (
          <div>
            <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
            <div className="type-subtext text-muted-foreground">Recorded {row.state}</div>
          </div>
        ),
    },
    {
      key: "retry-lineage",
      header: "Retry lineage",
      render: (row) => (
        <div>
          {row.supersedesAttemptId ? (
            <>
              <ToneBadge tone="unknown">RUN RETRY</ToneBadge>
              <div className="type-subtext text-muted-foreground">
                Repairs previous attempt <Mono>{row.supersedesAttemptId}</Mono>
              </div>
            </>
          ) : (
            <>
              <ToneBadge tone="neutral">INITIAL ATTEMPT</ToneBadge>
              <div className="type-subtext text-muted-foreground">
                No previous attempt superseded.
              </div>
            </>
          )}
          <div className="type-subtext text-muted-foreground">
            Attach before material execution is required for current authority.
          </div>
        </div>
      ),
    },
  ];
  const receiptColumns: DataTableColumn<Receipt>[] = [
    {
      key: "receipt",
      header: "Verified work",
      cellClassName: "whitespace-normal",
      render: (row) => {
        const work = readableWork.find((item) => item.id === row.payload.binding.workItemId);
        return (
          // Plain-language first: the row names what the verifier ruled on. Selecting the
          // receipt still works, and the ids stay on the row under "Reference IDs".
          <div className="min-w-0" data-receipt-id={row.payload.id}>
            <button
              className="text-left hover:underline"
              data-action-id="work.select-receipt"
              onClick={() => setSelectedReceiptId(row.payload.id)}
              data-receipt-plain
            >
              <strong>{work?.title ?? "Work item outside the current ledger"}</strong>
            </button>
            <details className="type-subtext text-muted-foreground">
              <summary className="cursor-pointer">Reference IDs</summary>
              <div className="mt-1 flex min-w-0 flex-col gap-0.5 break-all" data-receipt-ids>
                <Mono>Receipt {row.payload.id}</Mono>
                <Mono>Work {row.payload.binding.workItemId}</Mono>
                <Mono>Attempt {row.payload.binding.attemptId}</Mono>
              </div>
            </details>
          </div>
        );
      },
    },
    {
      key: "verdict",
      header: "Verdict",
      render: (row) =>
        prospectiveReceiptIds.has(row.payload.id) ? (
          <Verdict status={verdictFrom(resolvedVerdicts, row, now)} />
        ) : currentReceiptIds.has(row.payload.id) ? (
          <div>
            <ToneBadge tone="unknown">RETROSPECTIVE</ToneBadge>
            <div className="type-subtext text-muted-foreground">
              Recorded {verdictFrom(resolvedVerdicts, row, now)}
            </div>
          </div>
        ) : (
          <div>
            <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
            <div className="type-subtext text-muted-foreground">
              Recorded {verdictFrom(resolvedVerdicts, row, now)}
            </div>
          </div>
        ),
    },
    {
      key: "task-outcome",
      header: "Task outcome",
      render: (row) =>
        prospectiveReceiptIds.has(row.payload.id) && receiptCompletionSummary(row)?.taskOutcome ? (
          <Verdict status={receiptCompletionSummary(row)!.taskOutcome!.status} />
        ) : (
          <ToneBadge tone="unknown">
            {prospectiveReceiptIds.has(row.payload.id)
              ? "NOT APPLICABLE"
              : currentReceiptIds.has(row.payload.id)
                ? "RETROSPECTIVE"
                : "INCOMPLETE"}
          </ToneBadge>
        ),
    },
    {
      key: "baseline-health",
      header: "Baseline health",
      render: (row) =>
        prospectiveReceiptIds.has(row.payload.id) &&
        receiptCompletionSummary(row)?.baselineHealth ? (
          <Verdict status={receiptCompletionSummary(row)!.baselineHealth!.status} />
        ) : (
          <ToneBadge tone="unknown">
            {prospectiveReceiptIds.has(row.payload.id)
              ? "NOT APPLICABLE"
              : currentReceiptIds.has(row.payload.id)
                ? "RETROSPECTIVE"
                : "INCOMPLETE"}
          </ToneBadge>
        ),
    },
    {
      key: "assurance",
      header: "Assurance",
      render: (row) =>
        currentReceiptIds.has(row.payload.id) ? (
          <ToneBadge tone={row.payload.assurance === "PROSPECTIVE" ? "pass" : "unknown"}>
            {row.payload.assurance}
          </ToneBadge>
        ) : (
          <div>
            <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
            <div className="type-subtext text-muted-foreground">
              Recorded {row.payload.assurance}
            </div>
          </div>
        ),
    },
    {
      key: "binding",
      header: "Evidence binding",
      render: (row) => <Mono>{shortDigest(row.payload.binding.evidenceGraphDigest)}</Mono>,
    },
    {
      key: "checkpoint",
      header: "Checkpoint loop",
      render: (row) => (
        <div>
          <ToneBadge tone={prospectiveReceiptIds.has(row.payload.id) ? "pass" : "unknown"}>
            {prospectiveReceiptIds.has(row.payload.id) ? "CURRENT CHECKPOINT" : "HISTORY ONLY"}
          </ToneBadge>
          <div className="type-subtext text-muted-foreground">
            {row.payload.supersedesReceiptId ? (
              <>
                Supersedes prior receipt <Mono>{row.payload.supersedesReceiptId}</Mono>
              </>
            ) : (
              "No prior receipt superseded."
            )}
          </div>
          <div className="type-subtext text-muted-foreground">
            Checkpoint result stays console-visible; origin-session talkback requires adapter
            support.
          </div>
        </div>
      ),
    },
    {
      key: "signature",
      header: "Signature",
      // The trust root names this key once above; a row only reports whether it was
      // signed under that same key. The key id stays on the row.
      render: (row) => (
        <div className="min-w-0" data-signature-key-id={row.signature.keyId}>
          {row.signature.keyId === data?.trust.keyId ? (
            <span>Active trust key</span>
          ) : (
            <Mono>{row.signature.keyId}</Mono>
          )}
        </div>
      ),
    },
  ];
  const environmentBindingColumns: DataTableColumn<EnvironmentBindingProof>[] = [
    {
      key: "receipt",
      header: "Receipt",
      render: (row) => <Mono>{row.receiptId}</Mono>,
    },
    {
      key: "snapshots",
      header: "Complete snapshot binding",
      render: (row) => (
        <div className="space-y-1">
          <div>
            Contract <Mono>{shortDigest(row.contractDigest)}</Mono>
          </div>
          <div>
            Input <Mono>{shortDigest(row.inputSnapshotDigest)}</Mono>
          </div>
          <div>
            Output <Mono>{shortDigest(row.outputSnapshotDigest)}</Mono>
          </div>
        </div>
      ),
    },
    {
      key: "environment",
      header: "Signed environment",
      render: (row) => (
        <div>
          <Mono>{shortDigest(row.signedEnvironmentBindingDigest)}</Mono>
          <div className="type-subtext text-muted-foreground">
            Replay digest <Mono>{shortDigest(row.replayEnvironmentDigest)}</Mono>
          </div>
        </div>
      ),
    },
    {
      key: "capture",
      header: "Live capture requirement",
      render: (row) => (
        <div>
          <ToneBadge tone={row.currentAuthority ? "pass" : "unknown"}>
            {row.liveCaptureState === "current_receipt_bound"
              ? "CURRENT RECEIPT BOUND"
              : "HISTORY ONLY"}
          </ToneBadge>
          <div className="type-subtext text-muted-foreground">
            {row.requiredCaptureDimensions.length} dimensions required before receipt reuse:
            environment scope, execution boundary, capability inventory, locks, flags, migrations,
            provider account, configuration, evaluator versions, replay environment, and secret
            custody.
          </div>
          <div className="type-subtext text-muted-foreground">
            {row.refusalSemantics.replaceAll("-", " ")}
          </div>
        </div>
      ),
    },
  ];
  const amendmentColumns: DataTableColumn<Amendment>[] = [
    {
      key: "amendment",
      header: "Amendment",
      render: (row) => (
        <div>
          <Mono>{row.proposal.id}</Mono>
          <div className="type-subtext text-muted-foreground">{row.proposal.rationale}</div>
        </div>
      ),
    },
    {
      key: "proposer",
      header: "Proposed by",
      render: (row) => <Mono>{row.proposal.proposedBy.actorId}</Mono>,
    },
    {
      key: "impact",
      header: "Case impact",
      render: (row) =>
        currentAmendmentIds.has(row.proposal.id) ? (
          <Verdict status={row.proposal.caseImpact.status} />
        ) : (
          <div>
            <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
            <div className="type-subtext text-muted-foreground">
              Recorded {row.proposal.caseImpact.status}
            </div>
          </div>
        ),
    },
    {
      key: "state",
      header: "Decision",
      render: (row) =>
        !currentAmendmentIds.has(row.proposal.id) ? (
          <div>
            <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
            <div className="type-subtext text-muted-foreground">
              Recorded {row.decision?.decision ?? "PENDING"}
            </div>
          </div>
        ) : row.decision ? (
          <ToneBadge tone={row.decision.decision === "ACCEPTED" ? "pass" : "fail"}>
            {row.decision.decision}
          </ToneBadge>
        ) : (
          <ToneBadge tone="unknown">PENDING</ToneBadge>
        ),
    },
    {
      key: "actions",
      header: "",
      render: (row) =>
        row.decision ? null : (
          <div className="flex gap-1">
            <ActionButton
              actionId="universal.amendment.accept"
              size="sm"
              variant="accept"
              disabled={
                !canAuthorizeCurrentAction(
                  row.proposal.id,
                  currentAmendmentIds,
                  Boolean(selectedWorker) && busy === null,
                )
              }
              onClick={() => void decideAmendment(row, "ACCEPTED")}
            >
              <CheckCircle2 className="size-4" /> Accept
            </ActionButton>
            <ActionButton
              actionId="universal.amendment.reject"
              size="sm"
              variant="reject"
              disabled={
                !canAuthorizeCurrentAction(
                  row.proposal.id,
                  currentAmendmentIds,
                  Boolean(selectedWorker) && busy === null,
                )
              }
              onClick={() => void decideAmendment(row, "REJECTED")}
            >
              <Gavel className="size-4" /> Reject
            </ActionButton>
          </div>
        ),
    },
  ];
  const reviewColumns: DataTableColumn<Review>[] = [
    {
      key: "review",
      header: "Review",
      render: (row) => (
        <div>
          <Mono>{row.request.id}</Mono>
          <div className="type-subtext text-muted-foreground">{row.request.reason}</div>
        </div>
      ),
    },
    { key: "kind", header: "Kind", render: (row) => row.request.kind },
    {
      key: "requester",
      header: "Requested by",
      render: (row) => <Mono>{row.request.requestedBy.actorId}</Mono>,
    },
    {
      key: "state",
      header: "State",
      render: (row) =>
        !currentReviewIds.has(row.request.id) ? (
          <div>
            <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
            <div className="type-subtext text-muted-foreground">
              Recorded {row.resolution ? "RESOLVED" : "OPEN"}
            </div>
          </div>
        ) : row.resolution ? (
          <ToneBadge tone="pass">RESOLVED</ToneBadge>
        ) : (
          <ToneBadge tone="unknown">OPEN</ToneBadge>
        ),
    },
    {
      key: "action",
      header: "",
      render: (row) =>
        row.resolution ? null : (
          <ActionButton
            actionId="universal.review.resolve"
            size="sm"
            disabled={!canAuthorizeCurrentAction(row.request.id, currentReviewIds, busy === null)}
            reason={
              !currentReviewIds.has(row.request.id)
                ? "Historical reviews are incomplete and cannot authorize a resolution."
                : undefined
            }
            onClick={() => void resolveReview(row)}
          >
            <Gavel className="size-4" /> Resolve
          </ActionButton>
        ),
    },
  ];
  const learningColumns: DataTableColumn<LearningProposal>[] = [
    {
      key: "proposal",
      header: "Successor proposal",
      render: (row) => (
        <div>
          <Mono>{row.id}</Mono>
          <div className="type-subtext text-muted-foreground">
            {row.proposalKind.replaceAll("_", " ")}
          </div>
        </div>
      ),
    },
    {
      key: "source",
      header: "Verified source",
      render: (row) => (
        <div>
          <Mono>{row.sourceReceipt.id}</Mono>
          <div className="type-subtext text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <ToneBadge tone="unknown">HISTORY ONLY</ToneBadge>
              <span>Recorded {row.sourceReceipt.aggregateStatus}</span>
            </span>
          </div>
          <div className="type-subtext text-muted-foreground">
            Signed receipt · key <Mono>{row.sourceReceipt.signingKeyId}</Mono>
          </div>
          <div className="type-subtext text-muted-foreground">
            Payload <Mono>{shortDigest(row.sourceReceipt.payloadDigest)}</Mono> · determinations{" "}
            <Mono>{shortDigest(row.sourceReceipt.determinationSetDigest)}</Mono>
          </div>
          <div className="type-subtext text-muted-foreground">
            {learningSourceCalibration(row)} for calibration; worker transcript frequency is not a
            promotion source.
          </div>
        </div>
      ),
    },
    {
      key: "target",
      header: "Target",
      render: (row) => (
        <div>
          <Mono>{row.target.ref}</Mono>
          <div className="type-subtext text-muted-foreground">
            {row.target.baseVersion} → {row.target.successorVersion}
          </div>
        </div>
      ),
    },
    {
      key: "digest",
      header: "Proposal digest",
      render: (row) => <Mono>{shortDigest(row.proposalDigest)}</Mono>,
    },
    {
      key: "state",
      header: "State",
      render: (row) => (
        <div>
          <ToneBadge tone="unknown">{row.state}</ToneBadge>
          <div className="type-subtext text-muted-foreground">
            Proposal only · current truth mutation{" "}
            {row.currentTruthMutationAllowed ? "allowed" : "blocked"}
          </div>
        </div>
      ),
    },
    {
      key: "activate",
      header: "",
      render: () => (
        <ActionButton
          actionId="universal.learning.activate"
          size="sm"
          disabled
          data-browser-activation-fixture="learning-protected-successor-required"
          data-learning-activation-boundary="separate-accepted-successor-contract-or-policy"
          reason="Learning proposals are protected: activation requires a separate accepted successor contract or policy, not this history row."
        >
          Protected activation
        </ActionButton>
      ),
    },
  ];
  const completionTemplateColumns: DataTableColumn<CompletionTemplate>[] = [
    {
      key: "template",
      header: "Mechanical template",
      render: (row) => (
        <div>
          <Mono>{row.id}</Mono>
          <div className="type-subtext text-muted-foreground">
            {row.traceKind.replaceAll("_", " ")} · {row.criteria.length} criteria
          </div>
        </div>
      ),
    },
    {
      key: "absent",
      header: "Absent trace checks",
      render: (row) => (
        <div className="space-y-1">
          {row.expectedAbsentChecks.map((check) => (
            <div key={check}>
              <Mono>{check}</Mono>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: "alternatives",
      header: "Alternatives",
      render: (row) => (
        <div className="type-subtext text-muted-foreground">
          Bounded template catalog candidate; the engine selects mechanically or abstains when no
          coherent template is available.
        </div>
      ),
    },
  ];
  const completionProposalExampleColumns: DataTableColumn<CompletionProposalExample>[] = [
    {
      key: "trace",
      header: "Product trace",
      render: (row) => (
        <div>
          <strong>{row.label}</strong>
          <div className="type-subtext text-muted-foreground">{row.productVisibleMeaning}</div>
          <div className="type-subtext text-muted-foreground">
            Source trace <Mono>{shortDigest(row.trace.traceDigest)}</Mono>
          </div>
        </div>
      ),
    },
    {
      key: "selection",
      header: "Selection / abstention",
      render: (row) => (
        <div>
          <ToneBadge tone={row.proposal.state === "proposed" ? "pass" : "unknown"}>
            {row.proposal.state}
          </ToneBadge>
          <div className="type-subtext text-muted-foreground">
            Template{" "}
            {row.proposal.selectedTemplateId ? (
              <Mono>{row.proposal.selectedTemplateId}</Mono>
            ) : (
              "not selected"
            )}
          </div>
          <div className="type-subtext text-muted-foreground">
            Score {Math.round(row.proposal.evidenceBackedScore * 100)}%
          </div>
        </div>
      ),
    },
    {
      key: "claims",
      header: "Untrusted transcript claims",
      render: (row) => (
        <div>
          {row.trace.untrustedEventIds.length > 0 ? (
            row.trace.untrustedEventIds.map((eventId) => (
              <div key={eventId}>
                <Mono>{eventId}</Mono>
              </div>
            ))
          ) : (
            <span>None</span>
          )}
          <div className="type-subtext text-muted-foreground">
            Transcript-only success is evidence text, not an independent oracle.
          </div>
        </div>
      ),
    },
    {
      key: "gaps",
      header: "Named gaps",
      render: (row) => (
        <div className="space-y-1">
          {row.proposal.namedGaps.map((gap) => (
            <div key={gap}>
              <Mono>{gap}</Mono>
            </div>
          ))}
          <div className="type-subtext text-muted-foreground">
            Absent trace gaps remain visible before any contract acceptance.
          </div>
        </div>
      ),
    },
    {
      key: "activation",
      header: "Activation",
      render: (row) => (
        <div>
          <ToneBadge tone="unknown">
            {row.proposal.activatedContract ? "activated" : "not activated"}
          </ToneBadge>
          <div className="type-subtext text-muted-foreground">
            Proposal only; no Contract is activated from this row.
          </div>
        </div>
      ),
    },
  ];
  const adapterColumns: DataTableColumn<Adapter>[] = [
    { key: "adapter", header: "Adapter", render: (row) => <strong>{row.kind}</strong> },
    { key: "mode", header: "Mode", render: (row) => row.mode.replaceAll("_", " ") },
    {
      key: "consequence",
      header: "Declared actions",
      render: (row) => {
        const actions = row.supportedActions.filter((action) => action !== "none");
        const runtime = data?.adapterRuntime.find((candidate) => candidate.kind === row.kind);
        const delivery = runtime?.externalEffectsPossible ? "Live webhook possible" : "Intent only";
        const action = actions[0] ?? "none";
        return (
          <div data-adapter-capability={row.kind} data-adapter-consequence={row.kind}>
            <strong>
              {actions.length
                ? actions.map((action) => action.replaceAll("_", " ")).join(", ")
                : "No action"}
            </strong>
            <div className="type-subtext text-muted-foreground">
              Adapter profile only · no server action or target preview
            </div>
            {selectedReceipt ? (
              <div className="type-subtext text-muted-foreground">
                Consequence preview · {action.replaceAll("_", " ")} · {row.kind}://console/
                {selectedReceipt.payload.binding.workItemId} · {selectedReceipt.payload.id} ·{" "}
                {verdictFrom(resolvedVerdicts, selectedReceipt, now)} ·{" "}
                {selectedReceiptCanAuthorize ? "PROSPECTIVE" : "INCOMPLETE"} ·{" "}
                {shortDigest(selectedReceipt.payload.policy.digest)} ·{" "}
                {row.mode.replaceAll("_", " ")} · {delivery}
              </div>
            ) : (
              <div className="type-subtext text-muted-foreground">
                Consequence preview · INCOMPLETE · No signed receipt · {delivery}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "enforcement",
      header: "Runtime enforcement",
      render: (row) => {
        const runtime = data?.adapterRuntime.find((candidate) => candidate.kind === row.kind);
        const liveDeliveryProven =
          runtime != null && "liveDeliveryProven" in runtime && runtime.liveDeliveryProven === true;
        const runtimeEnforcementGuaranteed =
          runtime != null &&
          "enforcementGuaranteed" in runtime &&
          runtime.enforcementGuaranteed === true;
        const activeGuarantee =
          row.enforcementGuaranteed &&
          liveDeliveryProven &&
          runtimeEnforcementGuaranteed &&
          runtime?.delivery === "LIVE_WEBHOOK" &&
          runtime.externalEffectsPossible;
        return (
          <ToneBadge tone={activeGuarantee ? "pass" : "neutral"}>
            {activeGuarantee
              ? "GUARANTEED"
              : row.enforcementGuaranteed
                ? "PROFILE ONLY"
                : "NOT CLAIMED"}
          </ToneBadge>
        );
      },
    },
    {
      key: "delivery",
      header: "External effect",
      render: (row) => {
        const runtime = data?.adapterRuntime.find((candidate) => candidate.kind === row.kind);
        if (!runtime) return <ToneBadge tone="neutral">UNAVAILABLE</ToneBadge>;
        return (
          <ToneBadge tone={runtime?.externalEffectsPossible ? "unknown" : "neutral"}>
            {runtime?.externalEffectsPossible ? "LIVE DELIVERY DISABLED" : "INTENT ONLY"}
          </ToneBadge>
        );
      },
    },
    {
      key: "execute",
      header: "",
      render: (row) => {
        const runtime = data?.adapterRuntime.find((candidate) => candidate.kind === row.kind);
        const intentOnly =
          runtime?.delivery === "INTENT_ONLY" && runtime.externalEffectsPossible === false;
        return (
          <ActionButton
            actionId={`universal.adapter.${row.kind}`}
            size="sm"
            disabled={
              !intentOnly || !selectedReceipt || !selectedReceiptCanAuthorize || busy !== null
            }
            reason={
              !runtime
                ? "Runtime capability status is unavailable, so no adapter action can be issued."
                : !intentOnly
                  ? "Live delivery requires a server-backed action and target preview plus explicit confirmation."
                  : selectedReceipt && !selectedReceiptCanAuthorize
                    ? "Retrospective and incomplete receipts cannot authorize adapter execution."
                    : "Select a completion-bound receipt first."
            }
            onClick={() => void executeAdapter(row)}
          >
            <ShieldCheck className="size-4" />{" "}
            {!runtime ? "Runtime unavailable" : intentOnly ? "Issue intent" : "Delivery disabled"}
          </ActionButton>
        );
      },
    },
  ];
  const durableLoopJobColumns: DataTableColumn<DurableLoopJob>[] = [
    {
      key: "job",
      header: "Durable job",
      render: (row) => (
        <div>
          <Mono>{row.id}</Mono>
          <div className="type-subtext text-muted-foreground">
            {row.runId} · {row.attemptId}
          </div>
        </div>
      ),
    },
    {
      key: "lease",
      header: "Lease / heartbeat",
      render: (row) => (
        <div>
          <ToneBadge tone={row.state === "COMPLETED" ? "pass" : "unknown"}>{row.state}</ToneBadge>
          <div className="type-subtext text-muted-foreground">
            Owner <Mono>{row.leaseOwnerId ?? "none"}</Mono>
          </div>
          <div className="type-subtext text-muted-foreground">
            Heartbeat {row.heartbeatAt ? new Date(row.heartbeatAt).toLocaleString() : "none"}
          </div>
          <div className="type-subtext text-muted-foreground">
            Lease expires{" "}
            {row.leaseExpiresAt ? new Date(row.leaseExpiresAt).toLocaleString() : "none"}
          </div>
        </div>
      ),
    },
    {
      key: "resume",
      header: "Resume token",
      render: (row) => <Mono>{shortDigest(row.resumeToken)}</Mono>,
    },
    {
      key: "lineage",
      header: "Receipt lineage",
      render: (row) => (
        <div>
          {row.priorReceiptDigests.map((digest) => (
            <div key={digest}>
              <Mono>{shortDigest(digest)}</Mono>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: "idempotency",
      header: "Duplicate-effect guard",
      render: (row) => (
        <div>
          <Mono>{row.idempotencyKey}</Mono>
          <div className="type-subtext text-muted-foreground">
            {row.effectDigest ? (
              <>
                Effect <Mono>{shortDigest(row.effectDigest)}</Mono>
              </>
            ) : (
              "No effect committed"
            )}
          </div>
        </div>
      ),
    },
  ];
  const operationalJobColumns: DataTableColumn<OperationalJob>[] = [
    {
      key: "kind",
      header: "Job kind",
      render: (row) => <strong>{row.kind.replaceAll("_", " ")}</strong>,
    },
    {
      key: "scope",
      header: "Scope",
      render: (row) => (
        <div>
          <Mono>{row.tenantId}</Mono>
          <div className="type-subtext text-muted-foreground">
            <Mono>{row.environmentId}</Mono> · <Mono>{row.workflowId}</Mono>
          </div>
          <div className="type-subtext text-muted-foreground">
            Attempt <Mono>{row.attemptId}</Mono>
          </div>
        </div>
      ),
    },
    {
      key: "metrics",
      header: "Liveness",
      render: (row) => (
        <div>
          Queue {row.queueDelayMs}ms · collection {row.collectionLatencyMs}ms
          <div className="type-subtext text-muted-foreground">
            Evidence freshness {row.evidenceFreshnessMs}ms · confidence{" "}
            {Math.round(row.recurrenceMatchConfidence * 100)}%
          </div>
        </div>
      ),
    },
    {
      key: "delivery",
      header: "Delivery",
      render: (row) => (
        <div>
          <ToneBadge tone={row.deliveryState === "delivered" ? "pass" : "unknown"}>
            {row.deliveryState}
          </ToneBadge>
          <div className="type-subtext text-muted-foreground">
            Verifier {row.verifierAvailable ? "available" : "unavailable"} · duplicate effects{" "}
            {row.duplicateEffectSafe ? "prevented" : "unsafe"}
          </div>
        </div>
      ),
    },
    {
      key: "custody",
      header: "Evidence custody",
      render: (row) => (
        <div>
          <Mono>{shortDigest(row.rawEvidence.evidenceDigest)}</Mono>
          <div className="type-subtext text-muted-foreground">
            {row.rawEvidence.redactionState.replaceAll("_", " ")} · retained until{" "}
            {new Date(row.rawEvidence.retentionUntil).toLocaleDateString()}
          </div>
          <div className="type-subtext text-muted-foreground">
            Metadata <Mono>{shortDigest(row.rawEvidence.discoveryMetadataDigest)}</Mono>
          </div>
        </div>
      ),
    },
  ];

  if (!localEnvironmentReady) {
    return (
      <Callout tone="unknown" title="Canonical Environment context required">
        <p>
          A verified canonical Environment is required. Remote and offline rows appear only when
          the server has an exact durable Work attribution for this Environment.
        </p>
      </Callout>
    );
  }
  if (overview.error || (overview.data && !responseCurrent)) {
    return (
      <Callout
        tone="unknown"
        title={handoff ? "Worker attempt status unavailable" : "Universal protocol unavailable"}
      >
        <p>
          Current status could not be loaded. No new attempt is confirmed by this screen. Check your
          connection or sign in, then retry.
        </p>
        <details>
          <summary>Connection details</summary>
          <p className="break-all">
            {overview.error?.message ?? "Work response did not match the selected Environment."}
          </p>
        </details>
        <div className="mt-3 flex flex-wrap gap-2">
          <ActionButton actionId="universal.retry" onClick={() => void overview.mutate()}>
            Retry connection
          </ActionButton>
          <a
            href="/login"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-9 items-center px-3 underline"
          >
            Sign in in a new tab
          </a>
        </div>
      </Callout>
    );
  }
  if (overview.isLoading || !data || !completionProjection) {
    return (
      <Callout
        tone="neutral"
        title={handoff ? "Loading your accepted task" : "Loading universal protocol"}
      >
        Reading the tenant-scoped verification ledger.
      </Callout>
    );
  }

  if (handoff) {
    const contract = completionProjection.current.contracts.find(
      (item) =>
        item.contractDigest === handoff.contractDigest &&
        item.content.workItemId === handoff.workId,
    );
    const work = completionProjection.current.work.find((item) => item.id === handoff.workId);
    if (!contract || !work || work.state === "CLOSED" || !handoff.workerActorId)
      return (
        <Callout tone="unknown" title="Task needs review before a new attempt">
          <p>
            The selected task, accepted contract, or assigned worker is missing, no longer current,
            or closed. Open the saved task to inspect its status; return from its current accepted
            contract to continue.
          </p>
          <a
            className="underline"
            href={canonicalWorkHref(
              `/work-ledger/${encodeURIComponent(handoff.workId)}`,
              canonicalContext,
            )}
          >
            Inspect saved task
          </a>
        </Callout>
      );
    return (
      <CodingWorkerHandoff
        key={`${contract.contractDigest}:${handoff.workerActorId}`}
        contract={contract}
        workerActorId={handoff.workerActorId}
        workerAvailable={workers.some((worker) => worker.actorId === handoff.workerActorId)}
        attempts={completionProjection.current.attempts}
        onRefresh={() => overview.mutate()}
        workHref={canonicalWorkHref(
          `/work-ledger/${encodeURIComponent(handoff.workId)}`,
          canonicalContext,
        )}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-universal-verification-console="">
      <Section
        title="Universal verification"
        note={
          <>
            <Mono>{data.protocolVersion}</Mono> · generated{" "}
            {new Date(data.generatedAt).toLocaleString()}
          </>
        }
        actions={
          <ActionButton
            actionId="universal.refresh"
            size="sm"
            onClick={() => void overview.mutate()}
          >
            <RefreshCw className="size-4" /> Refresh
          </ActionButton>
        }
      >
        <StatGrid cols={4}>
          <Stat label="Current Work" value={completionProjection.current.work.length} />
          <Stat label="Current contracts" value={completionProjection.current.contracts.length} />
          <Stat
            label="Prospective receipts"
            value={prospectiveReceiptIds.size}
            tone={
              completionProjection.current.receipts.some(
                (receipt) =>
                  receipt.payload.assurance === "PROSPECTIVE" &&
                  verdictFrom(resolvedVerdicts, receipt, now) === "PASS",
              )
                ? "pass"
                : undefined
            }
          />
          <Stat
            label="Current open reviews"
            value={
              completionProjection.current.reviews.filter((review) => review.resolution === null)
                .length
            }
            tone={
              completionProjection.current.reviews.some((review) => review.resolution === null)
                ? "unknown"
                : undefined
            }
          />
        </StatGrid>
        {incompleteRecordCount > 0 ? (
          <Callout tone="unknown" title="Historical records are incomplete">
            {incompleteRecordCount} readable Work, contract, attempt, receipt, amendment, review, or
            learning record
            {incompleteRecordCount === 1 ? " is" : "s are"} outside the current completion chain.
            Recorded facts remain visible in their tables, but cannot authorize reviews, attempts,
            or adapter effects.
          </Callout>
        ) : null}
        <Callout
          tone="unknown"
          title="Unseen corpus release launch"
          data-cu401-release-corpus-launch=""
          data-proof-scope="requirements-only"
          data-release-eligible="false"
        >
          Release readiness has not been established. Fixture-only or unexecuted corpus evidence
          cannot authorize PASS or release. The quarantined unseen corpus still requires an
          unfamiliar workflow to complete discovery, authorized acceptance, and independent
          verification through the external public launch service. A disposable Node process over
          serialized public stdin, with no repo-package imports, sqlite, data DB, or user-home path
          dependence, proves only the transport boundary. Release requires zero false PASS from
          independently verified outcomes and measures discovery coverage separately from
          verification authority.
        </Callout>
        <Fields>
          <Field label="Dogfood freshness">
            <span className="inline-flex flex-wrap items-center gap-2">
              <ToneBadge
                tone={
                  data.dogfood.freshness === "FRESH"
                    ? "pass"
                    : data.dogfood.freshness === "MISSING"
                      ? "unknown"
                      : "fail"
                }
              >
                {data.dogfood.freshness}
              </ToneBadge>
              {data.dogfood.lastSuccessfulReceipt ? (
                <>
                  <Mono>{data.dogfood.lastSuccessfulReceipt.id}</Mono>
                  <span>
                    {new Date(data.dogfood.lastSuccessfulReceipt.issuedAt).toLocaleString()}
                  </span>
                </>
              ) : (
                "No successful signed canary receipt"
              )}
            </span>
          </Field>
          <Field label="Trust root">
            <span className="inline-flex flex-wrap items-center gap-2">
              <ToneBadge tone={data.trust.status === "active" ? "pass" : "fail"}>
                {data.trust.status}
              </ToneBadge>
              <Mono>{data.trust.keyId}</Mono>
              <Mono>{shortDigest(data.trust.fingerprint)}</Mono>
            </span>
          </Field>
          <Field label="Protocol policy">
            <span className="inline-flex flex-wrap items-center gap-2">
              <Mono>
                {data.protocolPolicy.ref}@{data.protocolPolicy.version}
              </Mono>
              <Mono>{shortDigest(data.protocolPolicyDigest)}</Mono>
            </span>
          </Field>
          <Field label="Worker principal">
            {workers.length ? (
              <select
                className={selectClass}
                value={selectedWorker}
                onChange={(event) => setWorkerActorId(event.target.value)}
              >
                {workers.map((worker) => (
                  <option key={worker.actorId} value={worker.actorId}>
                    {worker.actorId}
                  </option>
                ))}
              </select>
            ) : (
              <ToneBadge tone="unknown">NONE AUTHENTICATED</ToneBadge>
            )}
          </Field>
          <Field label="Selected receipt">
            {selectedReceipt ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                <Mono>{selectedReceipt.payload.id}</Mono>
                {!selectedReceiptIsCurrent ? (
                  <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
                ) : !selectedReceiptCanAuthorize ? (
                  <ToneBadge tone="unknown">RETROSPECTIVE · recorded</ToneBadge>
                ) : null}
              </span>
            ) : (
              "None"
            )}
          </Field>
        </Fields>
      </Section>

      <Section
        title="Environment & occurrence proof"
        note="CU-101/CU-102 product-visible status from the canonical overview transport."
      >
        <div
          data-cu101-environment-enrollment-browser-proof="universalVerification.overview"
          data-cu101-environment-enrollment-execution={data.environmentEnrollmentProof.execution}
          data-cu101-browser-fixture-execution={
            data.environmentEnrollmentProof.browserFixtureExecution
          }
          data-cu101-provider-adapter-execution={
            data.environmentEnrollmentProof.providerAdapterHarness.externalProviderExecution
          }
          data-cu101-provider-adapter-cases={
            data.environmentEnrollmentProof.providerAdapterHarness.casesRun
          }
          data-cu101-signed-provider-enrollment={
            data.environmentEnrollmentProof.providerAdapterHarness.signedProviderEnrollment
              .execution
          }
          data-cu101-fixture-count={data.environmentEnrollmentProof.fixtureMatrix.fixtures.length}
          data-cu101-coverage-digest={data.environmentEnrollmentProof.fixtureMatrix.coverageDigest}
          data-cu101-provider-service-integration={
            data.environmentEnrollmentProof.providerLifecycle.serviceIntegration
          }
          data-cu101-provider-active-claim={
            data.environmentEnrollmentProof.providerLifecycle.activeClaim
          }
        >
          <Fields>
            <Field label="Environment enrollment coverage">
              <span className="inline-flex flex-wrap items-center gap-2">
                <ToneBadge tone="neutral">{data.environmentEnrollmentProof.execution}</ToneBadge>
                <span>
                  {data.environmentEnrollmentProof.fixtureMatrix.fixtures.length} fixtures
                </span>
                <Mono>
                  {shortDigest(data.environmentEnrollmentProof.fixtureMatrix.coverageDigest)}
                </Mono>
              </span>
            </Field>
            <Field label="Identity boundaries">
              <Mono>{data.environmentEnrollmentProof.identityBoundariesVisible.join(", ")}</Mono>
            </Field>
            <Field label="Unsafe auto-links blocked">
              <Mono>{data.environmentEnrollmentProof.unsafeAutoLinksBlocked.join(", ")}</Mono>
            </Field>
            <Field label="Browser fixture execution">
              <span className="inline-flex flex-wrap items-center gap-2">
                <ToneBadge
                  tone={
                    data.environmentEnrollmentProof.browserFixtureExecution === "proven"
                      ? "pass"
                      : "unknown"
                  }
                >
                  {data.environmentEnrollmentProof.browserFixtureExecution}
                </ToneBadge>
                <span>
                  Fixture matrix is shipped in-product and exercised through a live browser session
                  against the canonical overview transport. Provider entries below execute a
                  field-derived loopback adapter and do not use third-party provider credentials.
                </span>
              </span>
            </Field>
            <Field label="Provider adapter harness">
              <span className="inline-flex flex-wrap items-center gap-2">
                <ToneBadge tone="pass">
                  {data.environmentEnrollmentProof.providerAdapterHarness.externalProviderExecution}
                </ToneBadge>
                <span>
                  {data.environmentEnrollmentProof.providerAdapterHarness.passedCases}/
                  {data.environmentEnrollmentProof.providerAdapterHarness.casesRun} field-derived
                  loopback provider cases: existing target, new target, missing target receipt,
                  collision, tenant confusion, and unknown target. The adapter resolves from
                  account, target, and receipt inputs in a fixed allowlist. These fixture results do
                  not enroll provider accounts or validate provider receipt signatures.
                </span>
                <Mono>
                  {shortDigest(
                    data.environmentEnrollmentProof.providerAdapterHarness.coverageDigest,
                  )}
                </Mono>
              </span>
            </Field>
            <Field label="Signed provider enrollment">
              <span className="inline-flex flex-wrap items-center gap-2">
                <ToneBadge
                  tone={
                    data.environmentEnrollmentProof.providerAdapterHarness.signedProviderEnrollment
                      .execution === "canonical_signed_envelope_verified"
                      ? "pass"
                      : "unknown"
                  }
                >
                  {
                    data.environmentEnrollmentProof.providerAdapterHarness.signedProviderEnrollment
                      .execution
                  }
                </ToneBadge>
                <span>
                  The canonical signed provider enrollment path validates exact canonical bytes,
                  provider-directory signer purpose, audience, signing domain, role-bound
                  registration, expiry, and cryptographic signature before enrollment resolution.
                  With an explicit storage driver, that path can persist a canonical enrolling,
                  pinned Environment. This overview runs unsigned fixture resolution only and does
                  not invoke that signed enrollment path. Product service integration and
                  authenticated third-party provider credentials and receipts remain required.
                </span>
              </span>
            </Field>
            <Field label="Provider activation">
              <span className="inline-flex flex-wrap items-center gap-2">
                <ToneBadge tone="unknown">
                  {data.environmentEnrollmentProof.providerLifecycle.activeClaim}
                </ToneBadge>
                <Mono>{data.environmentEnrollmentProof.providerLifecycle.serviceIntegration}</Mono>
                <span>
                  No persisted provider enrollment, activation receipt, or first provider projection
                  is observed by this overview. Fixture links and coverage digests cannot establish
                  an active Environment. Persisting an enrolling identity alone does not prove
                  activation.
                </span>
              </span>
            </Field>
            <Field label="Runtime occurrence boundary">
              <span className="inline-flex flex-wrap items-center gap-2">
                <ToneBadge
                  tone={
                    data.occurrenceBoundaryProof.boundary.state === "BOUND" ? "pass" : "unknown"
                  }
                >
                  {data.occurrenceBoundaryProof.boundary.state}
                </ToneBadge>
                <span>{data.occurrenceBoundaryProof.boundary.confidence} confidence</span>
                <Mono>
                  {shortDigest(data.occurrenceBoundaryProof.boundary.stableOccurrenceDigest)}
                </Mono>
              </span>
            </Field>
            <Field label="Projected adapter signals">
              <Mono>
                {data.occurrenceBoundaryProof.projectedSignals
                  .map((signal) => signal.kind)
                  .join(", ")}
              </Mono>
            </Field>
            <Field label="Completion truth separation">
              <span className="inline-flex flex-wrap items-center gap-2">
                <ToneBadge tone="unknown">
                  success established:{" "}
                  {String(data.occurrenceBoundaryProof.boundary.successfulCompletionEstablished)}
                </ToneBadge>
                <span>
                  Worker done claims segment the occurrence; an accepted contract and receipt are
                  still required for completion truth.
                </span>
              </span>
            </Field>
            <Field label="Adapter ingestion status">
              <span className="inline-flex flex-wrap items-center gap-2">
                <ToneBadge tone="neutral">
                  {data.occurrenceBoundaryProof.liveAdapterIngestion}
                </ToneBadge>
                <span>{data.occurrenceBoundaryProof.automaticApplicationAuthority}</span>
              </span>
            </Field>
          </Fields>
        </div>
      </Section>

      {error ? (
        <Callout tone="fail" title="Operation refused">
          {error}
        </Callout>
      ) : null}
      {notice ? (
        <Callout tone="pass" title="Operation recorded">
          {notice}
        </Callout>
      ) : null}

      <ScopeTabs
        value={tab}
        onChange={setTab}
        actionId="universal.tabs"
        options={[
          { key: "work", label: "Work", actionId: "universal.tab.work" },
          { key: "contracts", label: "Contracts", actionId: "universal.tab.contracts" },
          { key: "attempts", label: "Attempts", actionId: "universal.tab.attempts" },
          { key: "receipts", label: "Receipts", actionId: "universal.tab.receipts" },
          { key: "reviews", label: "Reviews", actionId: "universal.tab.reviews" },
          { key: "adapters", label: "Adapters", actionId: "universal.tab.adapters" },
          { key: "operations", label: "Operations", actionId: "universal.tab.operations" },
        ]}
      />

      {tab === "work" ? (
        <>
          <Section title="Create work">
            <form
              className="grid grid-cols-1 gap-3 border-y border-border py-4 md:grid-cols-2"
              onSubmit={(event) => void createWork(event)}
            >
              <div className={fieldClass}>
                <Label htmlFor="universal-work-title">Title</Label>
                <Input
                  id="universal-work-title"
                  required
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="universal-work-type">Work type</Label>
                <Input
                  id="universal-work-type"
                  required
                  value={workType}
                  onChange={(event) => setWorkType(event.target.value)}
                />
              </div>
              <div className={`${fieldClass} md:col-span-2`}>
                <Label htmlFor="universal-work-description">Description</Label>
                <Textarea
                  id="universal-work-description"
                  required
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </div>
              <div className={`${fieldClass} md:col-span-2`}>
                <Label htmlFor="universal-work-source">Source URI</Label>
                <Input
                  id="universal-work-source"
                  required
                  value={sourceUri}
                  onChange={(event) => setSourceUri(event.target.value)}
                />
              </div>
              <div>
                <ActionButton
                  type="submit"
                  actionId="universal.create-work"
                  variant="primary"
                  disabled={busy !== null}
                >
                  <Plus className="size-4" /> Create work
                </ActionButton>
              </div>
            </form>
          </Section>
          <Section
            title="Work"
            actions={
              <ScopeTabs
                value={workSubjectFilter}
                onChange={setWorkSubjectFilter}
                options={[
                  {
                    key: "all",
                    label: `All (${readableWork.length})`,
                    actionId: "work.subject-filter-all",
                  },
                  {
                    key: "real-subject",
                    label: `Real subject (${workSubjectCounts["real-subject"]})`,
                    actionId: "work.subject-filter-real-subject",
                  },
                  {
                    key: "self-test-fixture",
                    label: `Self-test (${workSubjectCounts["self-test-fixture"]})`,
                    actionId: "work.subject-filter-self-test-fixture",
                  },
                  {
                    key: "synthetic-fixture",
                    label: `Synthetic (${workSubjectCounts["synthetic-fixture"]})`,
                    actionId: "work.subject-filter-synthetic-fixture",
                  },
                ]}
              />
            }
          >
            <DataTable columns={workColumns} rows={filteredWork} rowKey={(row) => row.id} />
          </Section>
        </>
      ) : null}

      {tab === "contracts" ? (
        <>
          <Section title="Compile and accept">
            <div
              data-cu002-resolver-backed-admission=""
              data-cu002-admission-action="universal.compile-contract"
            >
              <Callout tone="unknown" title="Resolver-backed admission">
                Compile runs the live admission resolver before any contract is accepted. Missing
                evaluators, unavailable collectors, incompatible adapters, impossible independence,
                source conflicts, cost, latency, or freshness failures stay as rejected obligations
                with executed-check reason codes instead of silent auto-admission. Repository
                capability-map capture is attached to the admission and compilation record so
                repository shape, selected commands, preserved safety gates, and unsupported
                boundaries travel with the accepted contract instead of remaining intake-only copy.
              </Callout>
            </div>
            <form
              className="grid grid-cols-1 gap-3 border-y border-border py-4 md:grid-cols-2"
              onSubmit={(event) => void compileContract(event)}
            >
              <div className={fieldClass}>
                <Label htmlFor="universal-contract-work">Work</Label>
                <select
                  id="universal-contract-work"
                  className={selectClass}
                  value={selectedWork}
                  onChange={(event) => setWorkItemId(event.target.value)}
                >
                  {completionProjection.current.work.map((work) => (
                    <option key={work.id} value={work.id}>
                      {work.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className={fieldClass}>
                <Label htmlFor="universal-predicate-kind">Predicate</Label>
                <select
                  id="universal-predicate-kind"
                  className={selectClass}
                  value={predicateKind}
                  onChange={(event) => setPredicateKind(event.target.value as typeof predicateKind)}
                >
                  <option value="exit_code">Exit code</option>
                  <option value="text_contains">Text contains</option>
                  <option value="artifact_present">Artifact present</option>
                </select>
              </div>
              <div className={`${fieldClass} md:col-span-2`}>
                <Label htmlFor="universal-predicate-description">Observable condition</Label>
                <Input
                  id="universal-predicate-description"
                  required
                  value={predicateDescription}
                  onChange={(event) => setPredicateDescription(event.target.value)}
                />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="universal-expected-value">Expected value</Label>
                <Input
                  id="universal-expected-value"
                  required={predicateKind !== "artifact_present"}
                  value={expectedValue}
                  onChange={(event) => setExpectedValue(event.target.value)}
                />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="universal-evidence-kind">Evidence kind</Label>
                <Input
                  id="universal-evidence-kind"
                  required
                  value={evidenceKind}
                  onChange={(event) => setEvidenceKind(event.target.value)}
                />
              </div>
              <div>
                <ActionButton
                  type="submit"
                  actionId="universal.compile-contract"
                  variant="primary"
                  disabled={!selectedWorkRecord || !selectedWorker || busy !== null}
                  reason="Current completion-aware Work and an authenticated worker are required."
                >
                  <FileCheck2 className="size-4" /> Compile and accept
                </ActionButton>
              </div>
            </form>
          </Section>
          <DataTable
            columns={contractColumns}
            rows={readableContracts}
            rowKey={(row) => row.contractDigest}
          />
        </>
      ) : null}

      {tab === "attempts" ? (
        <>
          <Callout tone="unknown" title="Automatic recurring loop">
            Later matching occurrences attach the accepted contract before material execution when
            the adapter supports it. Retry attempts remain explicit lineage and do not rewrite the
            failed attempt.
          </Callout>
          <DataTable
            columns={attemptColumns}
            rows={readableAttempts}
            rowKey={(row) => row.binding.attemptId}
          />
        </>
      ) : null}

      {tab === "receipts" ? (
        <>
          <Callout tone="unknown" title="Checkpoint, talkback, and supersession">
            Checkpoints stay visible in this console, return talkback only when the originating
            adapter channel supports it, retain prior receipt lineage on retry, and degrade timeout
            or disconnected environments to UNKNOWN/NOT_RUN instead of optimistic PASS.
          </Callout>
          <Callout tone="neutral" title="Complete snapshot and environment binding">
            Product receipts expose the CU-202 live-capture boundary: contract, input, and output
            snapshots must match the submitted attempt, the replay digest is the signed environment
            binding digest, and missing or changed live-captured environment dimensions keep receipt
            reuse ineligible instead of silently carrying PASS forward.
          </Callout>
          <div data-cu201-independent-replay-browser-path>
            <Section title="Independent collection and replay">
              <Callout
                tone={replayProofTone(data.independentReplayProof)}
                title="CU-201 replay boundary"
              >
                The product reads admitted evidence bundles bound to signed receipts, separates
                independent collector artifacts from worker-authored supporting logs, counts replay
                commands captured by the live adapter path, and exposes refusal reasons for changed
                commands, wrong submitted trees, stale or inaccessible artifacts, unverified bytes,
                shared collector identity, and external effects without a system of record.
              </Callout>
              <StatGrid cols={4}>
                <Stat
                  label="Evidence bundles"
                  value={data.independentReplayProof.evidenceBundleCount}
                />
                <Stat
                  label="Replay commands"
                  value={data.independentReplayProof.replayCommandCount}
                />
                <Stat
                  label="Independent artifacts"
                  value={data.independentReplayProof.independentArtifactCount}
                  tone={data.independentReplayProof.independentArtifactCount ? "pass" : "unknown"}
                />
                <Stat
                  label="Unverified artifacts"
                  value={data.independentReplayProof.unverifiedArtifactCount}
                  tone={data.independentReplayProof.unverifiedArtifactCount ? "unknown" : "pass"}
                />
              </StatGrid>
              <Fields>
                <Field label="Supporting artifacts">
                  {data.independentReplayProof.supportingArtifactCount}
                </Field>
                <Field label="Bytes verified">
                  {data.independentReplayProof.bytesVerifiedArtifactCount}
                </Field>
                <Field label="Observed gaps">
                  {data.independentReplayProof.gapReasonCodes.length ? (
                    <Mono>
                      <span data-cu201-replay-gap-codes>
                        {data.independentReplayProof.gapReasonCodes.join(", ")}
                      </span>
                    </Mono>
                  ) : (
                    "None"
                  )}
                </Field>
                <Field label="Replay limits">
                  <Mono>
                    <span data-cu201-replay-limitation-codes>
                      {data.independentReplayProof.limitationReasonCodes.join(", ")}
                    </span>
                  </Mono>
                </Field>
              </Fields>
            </Section>
          </div>
          <DataTable
            columns={receiptColumns}
            rows={readableReceipts}
            rowKey={(row) => row.payload.id}
          />
          {data.environmentBindingProof.length ? (
            <Section title="Environment binding proof">
              <DataTable
                columns={environmentBindingColumns}
                rows={data.environmentBindingProof}
                rowKey={(row) => row.receiptId}
              />
            </Section>
          ) : null}
          {selectedReceipt && receiptDetail.data ? (
            <Section title="Signed receipt detail">
              <Fields>
                <Field label="Payload digest">
                  <Mono>{receiptDetail.data.receipt.payloadDigest}</Mono>
                </Field>
                <Field label="Binding">
                  <Mono>{JSON.stringify(receiptDetail.data.receipt.payload.binding)}</Mono>
                </Field>
                <Field label="Replay">
                  {receiptDetail.data.receipt.payload.replay.status} ·{" "}
                  <Mono>{receiptDetail.data.receipt.payload.replay.environmentDigest}</Mono>
                </Field>
                <Field label="Assurance projection">
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <ToneBadge
                      tone={
                        receiptDetail.data.assurance.currentReceiptEligible ? "pass" : "unknown"
                      }
                    >
                      {receiptDetail.data.assurance.displayLabel}
                    </ToneBadge>
                    <Mono>{receiptDetail.data.assurance.assuranceClass}</Mono>
                    <span>{receiptDetail.data.assurance.lifecycleState}</span>
                  </span>
                </Field>
                <Field label="Assurance blockers">
                  {receiptDetail.data.assurance.blockedReasons.length ? (
                    <Mono>{receiptDetail.data.assurance.blockedReasons.join(", ")}</Mono>
                  ) : (
                    "None"
                  )}
                </Field>
                <Field label="Completion classes">
                  <Mono>
                    {selectedReceiptCanAuthorize &&
                    receiptCompletionSummary(receiptDetail.data.receipt)
                      ? JSON.stringify(receiptCompletionSummary(receiptDetail.data.receipt))
                      : selectedReceiptIsCurrent
                        ? "Retrospective record — no prospective completion authority"
                        : "Historical record — current completion chain incomplete"}
                  </Mono>
                </Field>
                <Field label="Signature">
                  <Mono>
                    {receiptDetail.data.receipt.signature.algorithm}:
                    {receiptDetail.data.receipt.signature.value}
                  </Mono>
                </Field>
                <Field label="Evidence artifacts">
                  {receiptDetail.data.evidence.artifacts.map((artifact) => (
                    <div key={artifact.artifact.id}>
                      <Mono>{artifact.artifact.id}</Mono> · {artifact.independence.classification} ·{" "}
                      {artifact.bytesVerified ? "bytes verified" : "bytes unverified"}
                    </div>
                  ))}
                </Field>
                <Field label="Determinations">
                  {receiptDetail.data.receipt.payload.determinations.map((determination) => (
                    <div key={determination.id} className="flex items-start gap-2 py-1">
                      <ToneBadge tone="neutral">
                        {("classification" in determination
                          ? determination.classification
                          : "legacy_unbound"
                        ).replaceAll("_", " ")}
                      </ToneBadge>
                      {selectedReceiptCanAuthorize ? (
                        <Verdict status={determination.status} />
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <ToneBadge tone="unknown">
                            {selectedReceiptIsCurrent ? "RETROSPECTIVE" : "INCOMPLETE"}
                          </ToneBadge>
                          <span className="type-subtext text-muted-foreground">
                            Recorded {determination.status}
                          </span>
                        </span>
                      )}
                      <span>{determination.rationale}</span>
                    </div>
                  ))}
                </Field>
              </Fields>
            </Section>
          ) : null}
          <Section title="Open review">
            <form
              className="grid grid-cols-1 gap-3 border-y border-border py-4 md:grid-cols-[12rem_minmax(0,1fr)_auto]"
              onSubmit={(event) => void requestReview(event)}
            >
              <div className={fieldClass}>
                <Label htmlFor="universal-review-kind">Kind</Label>
                <select
                  id="universal-review-kind"
                  className={selectClass}
                  value={reviewKind}
                  onChange={(event) => setReviewKind(event.target.value as typeof reviewKind)}
                >
                  <option value="APPEAL">Appeal</option>
                  <option value="DISPUTE">Dispute</option>
                  <option value="HUMAN_CHECK">Human check</option>
                </select>
              </div>
              <div className={fieldClass}>
                <Label htmlFor="universal-review-reason">Reason or check instruction</Label>
                <Input
                  id="universal-review-reason"
                  required
                  value={reviewReason}
                  onChange={(event) => setReviewReason(event.target.value)}
                />
              </div>
              <div className="self-end">
                <ActionButton
                  type="submit"
                  actionId="universal.request-review"
                  disabled={!selectedReceipt || !selectedReceiptIsCurrent || busy !== null}
                  reason={
                    selectedReceipt && !selectedReceiptIsCurrent
                      ? "Historical receipts are incomplete and cannot authorize a review."
                      : undefined
                  }
                >
                  <Gavel className="size-4" /> Open
                </ActionButton>
              </div>
            </form>
          </Section>
        </>
      ) : null}

      {tab === "reviews" ? (
        <>
          <div className="flex max-w-xs flex-col gap-1.5">
            <Label htmlFor="universal-human-status">Human determination</Label>
            <select
              id="universal-human-status"
              className={selectClass}
              value={humanStatus}
              onChange={(event) => setHumanStatus(event.target.value as typeof humanStatus)}
            >
              <option value="PASS">Pass</option>
              <option value="FAIL">Fail</option>
              <option value="UNKNOWN">Unknown</option>
              <option value="NOT_RUN">Not run</option>
            </select>
          </div>
          <DataTable
            columns={reviewColumns}
            rows={readableReviews}
            rowKey={(row) => row.request.id}
          />
          <Section title="Contract amendments">
            <DataTable
              columns={amendmentColumns}
              rows={readableAmendments}
              rowKey={(row) => row.proposal.id}
            />
          </Section>
          <Section title="Learning proposals">
            <Callout tone="unknown" title="Learning is proposal-only">
              Product learning is sourced from signed receipts and admitted review/evidence
              outcomes. It can retain negative outcomes for calibration, but it cannot self-activate
              a protected contract or policy. Downstream outcomes enter the corpus only when they
              are causally bound to the attempt; raw browser/session frequency remains hypothesis
              data and cannot promote criteria.
            </Callout>
            <DataTable
              columns={learningColumns}
              rows={readableLearningProposals}
              rowKey={(row) => row.id}
            />
          </Section>
          {completionProposalEngine ? (
            <div
              data-cu106-proposal-engine=""
              data-cu106-activation-boundary={completionProposalEngine.activationBoundary}
              data-cu106-transcript-claim-authority={
                completionProposalEngine.transcriptClaimAuthority
              }
              data-cu106-template-count={completionProposalEngine.templates.length}
              data-cu106-example-count={completionProposalEngine.examples.length}
            >
              <Section title="Completion proposal engine">
                <Callout tone="unknown" title="Mechanical proposals cannot self-activate">
                  Template selection, abstention, transcript-only untrusted claims, alternatives,
                  and absent trace gaps are shown from the product API. This surface is read-only:
                  activation boundary is{" "}
                  <span data-cu106-activation-boundary="">
                    {completionProposalEngine.activationBoundary}
                  </span>
                  , and transcript claim authority is{" "}
                  <span data-cu106-transcript-claim-authority="">
                    {completionProposalEngine.transcriptClaimAuthority}
                  </span>
                  .
                </Callout>
                <DataTable
                  columns={completionTemplateColumns}
                  rows={completionProposalEngine.templates}
                  rowKey={(row) => row.id}
                />
                <DataTable
                  columns={completionProposalExampleColumns}
                  rows={completionProposalEngine.examples}
                  rowKey={(row) => row.proposal.proposalId}
                />
              </Section>
            </div>
          ) : null}
        </>
      ) : null}

      {tab === "adapters" ? (
        <>
          <Section
            title="Adapter capabilities"
            note="Declared profiles with receipt-bound consequence preview; the server does not return an action or target preview"
          >
            <Fields>
              <Field label="Signed basis">
                {selectedReceipt ? (
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <Mono>{selectedReceipt.payload.id}</Mono>
                    {selectedReceiptCanAuthorize ? (
                      <>
                        <Verdict status={verdictFrom(resolvedVerdicts, selectedReceipt, now)} />
                        <ToneBadge
                          tone={
                            selectedReceipt.payload.assurance === "PROSPECTIVE" ? "pass" : "unknown"
                          }
                        >
                          {selectedReceipt.payload.assurance}
                        </ToneBadge>
                      </>
                    ) : selectedReceiptIsCurrent ? (
                      <>
                        <ToneBadge tone="unknown">RETROSPECTIVE · recorded</ToneBadge>
                        <span className="type-subtext text-muted-foreground">
                          Recorded {verdictFrom(resolvedVerdicts, selectedReceipt, now)}
                        </span>
                      </>
                    ) : (
                      <>
                        <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
                        <span className="type-subtext text-muted-foreground">
                          Historical fields: {verdictFrom(resolvedVerdicts, selectedReceipt, now)} ·{" "}
                          {selectedReceipt.payload.assurance}
                        </span>
                      </>
                    )}
                  </span>
                ) : (
                  "No signed receipt is available"
                )}
              </Field>
              <Field label="Policy mapping">
                <span>PASS → ALLOW · FAIL → BLOCK · UNKNOWN → HOLD · NOT RUN → RETURN</span>
              </Field>
              <Field label="Assurance gate">
                A non-prospective PASS is held; it cannot authorize an action.
              </Field>
              <Field label="Decision authority">
                <span className="inline-flex flex-wrap items-center gap-2">
                  <Mono>{data.currentActor.actorId}</Mono>
                  <span>{data.currentActor.roles.join(", ")}</span>
                  <Mono>{data.currentActor.executionBoundaryId}</Mono>
                </span>
              </Field>
              <Field label="Custody">
                Excellent issues a signed signal and never takes custody. Live webhook delivery is
                disabled until the server previews the exact action and target and the operator
                explicitly confirms both. Intent-only rows record an intent without external
                delivery.
              </Field>
            </Fields>
          </Section>
          <DataTable columns={adapterColumns} rows={data.adapters} rowKey={(row) => row.kind} />
          <Section title="Effect attempts">
            {data.effects.length ? (
              <>
                <Callout tone="unknown" title="Effect history has no completion join">
                  The overview does not expose the decision and intent records needed to prove an
                  effect belongs to a current receipt. Delivery facts remain readable, but are not
                  completion authority.
                </Callout>
                <Fields>
                  {data.effects.map((effect) => (
                    <Field
                      key={effect.id}
                      label={`${effect.connectorRef} #${effect.attemptNumber}`}
                    >
                      <span className="inline-flex flex-wrap items-center gap-2">
                        <ToneBadge tone="unknown">RECORDED {effect.state}</ToneBadge>
                        <Mono>{shortDigest(effect.idempotencyKey)}</Mono>
                        {effect.externalRef ?? "No external reference"}
                      </span>
                    </Field>
                  ))}
                </Fields>
              </>
            ) : (
              <Callout tone="neutral" title="No effect attempts">
                No connector has reported an external effect.
              </Callout>
            )}
          </Section>
        </>
      ) : null}

      {tab === "operations" ? (
        <div
          className="flex flex-col gap-6"
          data-cu403-operational-durability-panel=""
          data-cu403-evidence-origin={data.operationalEvidenceOrigin ?? "not_observed"}
          data-cu403-durable-loop-job-count={data.durableLoopJobs.length}
          data-cu403-operational-job-count={data.operationalDurability.projections.length}
          data-cu403-report-digest={data.operationalDurability.reportDigest}
        >
          <Section
            title="Durable loop job visibility"
            note="Durable state is shown when available. Synthetic canary records do not establish collector, verifier, or delivery execution."
          >
            <Callout tone="unknown" title="Scheduler evidence">
              {data.operationalEvidenceOrigin === "product_local_store"
                ? "Product local scheduler store. These rows were persisted by the universal verification server and replayed from durable operational state."
                : data.operationalEvidenceOrigin === "synthetic_fixture"
                  ? "Persisted synthetic canary examples. Completion, delivery, receipts, and timestamps were generated for illustration; no collector or verifier execution is established."
                  : "No scheduler execution has been observed on this surface."}
            </Callout>
            <Callout tone="unknown" title="Operational projection, not completion authority">
              These rows explain runner durability and observability. They do not turn a Work item
              into PASS; completion still requires the current prospective Receipt chain.
            </Callout>
            <StatGrid cols={4}>
              <Stat
                label="Duplicate effects"
                value={
                  data.operationalEvidenceOrigin !== "product_local_store"
                    ? "Not established"
                    : data.operationalDurability.duplicateEffectsPrevented
                      ? "Prevented"
                      : "Open"
                }
                tone={
                  data.operationalEvidenceOrigin !== "product_local_store"
                    ? "unknown"
                    : data.operationalDurability.duplicateEffectsPrevented
                      ? "pass"
                      : "fail"
                }
              />
              <Stat
                label="Scoped jobs"
                value={
                  data.operationalEvidenceOrigin !== "product_local_store"
                    ? "Not established"
                    : data.operationalDurability.allJobsScoped
                      ? "All scoped"
                      : "Gaps"
                }
                tone={
                  data.operationalEvidenceOrigin !== "product_local_store"
                    ? "unknown"
                    : data.operationalDurability.allJobsScoped
                      ? "pass"
                      : "fail"
                }
              />
              <Stat
                label="Observable jobs"
                value={
                  data.operationalEvidenceOrigin !== "product_local_store"
                    ? "Not established"
                    : data.operationalDurability.allJobsObservable
                      ? "All visible"
                      : "Gaps"
                }
                tone={
                  data.operationalEvidenceOrigin !== "product_local_store"
                    ? "unknown"
                    : data.operationalDurability.allJobsObservable
                      ? "pass"
                      : "fail"
                }
              />
              <Stat
                label="Raw custody"
                value={
                  data.operationalEvidenceOrigin !== "product_local_store"
                    ? "Not established"
                    : data.operationalDurability.allRawEvidenceCustodied
                      ? "Custodied"
                      : "Missing"
                }
                tone={
                  data.operationalEvidenceOrigin !== "product_local_store"
                    ? "unknown"
                    : data.operationalDurability.allRawEvidenceCustodied
                      ? "pass"
                      : "fail"
                }
              />
            </StatGrid>
          </Section>
          <div data-cu403-durable-loop-jobs="">
            <DataTable
              columns={durableLoopJobColumns}
              rows={data.durableLoopJobs}
              rowKey={(row) => `${row.id}:${row.state}:${row.updatedAt}`}
            />
          </div>
          <Section
            title="Operational job projections"
            note={`Digest ${shortDigest(data.operationalDurability.reportDigest)} · job kinds ${data.operationalDurability.jobKindsCovered.join(", ")}`}
          >
            <div data-cu403-operational-projections="">
              <DataTable
                columns={operationalJobColumns}
                rows={data.operationalDurability.projections}
                rowKey={(row) => row.id}
              />
            </div>
          </Section>
        </div>
      ) : null}
    </div>
  );
}
